import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Eye, 
  EyeOff, 
  Plus, 
  Trash2, 
  Save,
  Send,
  ChevronDown,
  CheckCircle,
  User,
  FileText,
  ShoppingCart,
  Calendar,
  Smartphone,
  Monitor,
  Settings,
  AlertCircle,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Building2,
  Palette,
  Type,
  MoreHorizontal,
  DollarSign,
  CreditCard,
  Moon,
  Sun,
  Heart,
  MessageSquare,
  X
} from 'lucide-react';
import { useInvoice } from '../contexts/InvoiceContext';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useCurrency } from '../contexts/CurrencyContext';
import Layout from '../components/NewLayout';
import SeoLandingContent from '../components/SeoLandingContent';
import { formatDate } from '../common/utils/dateUtils';
import { isMinimalInvoiceEmailValid } from '../common/utils/validation';
import { InvoiceLivePreview } from '../components/invoice-creator-v2/InvoiceLivePreview';
import { validateIBAN, validateBIC, validateSortCode, formatIBAN, formatSortCode } from '../utils/paymentValidation';
import StartHereGuide, { START_HERE_TUTORIAL_STEPS } from '../components/invoices/StartHereGuide';
import {
  enqueueUsageEvent,
  flushAnalyticsQueue,
  startUsageHeartbeat,
  getAnalyticsContext,
} from '../analytics/usageAnalytics';

const STRIPE_DONATE_URL = 'https://donate.stripe.com/6oU00j7XVd9kdUzasH7wA00';
const FEEDBACK_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfxf0wtQozfzJXTSsrJpNjCfSQzubVa32iS_TcGWubMN2Fckw/viewform?usp=publish-editor';

/** Desktop header pills — same as theme toggle + Hide/Show preview (md+ only; mobile uses its own styles) */
const DESKTOP_HEADER_NAV_PILL_CLASS =
  'group flex items-center space-x-2 px-3 py-2 rounded-[20px] text-sm font-medium transition-all duration-200 border border-transparent nav-text hover:border-white/30 focus:outline-none';

const DESKTOP_HEADER_NAV_PILL_STYLE = {
  background: 'transparent',
  height: '36px',
  minWidth: 'fit-content',
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const TUTORIAL_SECTION_ERROR_KEYS = {
  business: [
    'businessName',
    'businessEmail',
    'businessWebsite',
    'businessAddress',
    'businessCity',
    'businessState',
    'businessZipCode',
  ],
  client: [
    'customerName',
    'customerEmail',
    'customerAddress',
    'customerCity',
    'customerState',
    'customerZipCode',
  ],
  invoiceDetails: ['dueDate'],
  paymentDetails: [],
  lineItems: ['lineItems', 'subtotal'],
};

const StartHereButton = ({ isDark, mobile = false, onClick }) => {
  const className = mobile
    ? 'group flex items-center justify-center gap-1.5 rounded-[20px] border px-3 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-0 shrink-0'
    : DESKTOP_HEADER_NAV_PILL_CLASS;

  const style = mobile
    ? {
        background: isDark ? 'rgba(167, 238, 67, 0.12)' : '#ffffff',
        color: isDark ? '#edff00' : '#1a1a1a',
        borderColor: isDark ? 'rgba(237, 255, 0, 0.5)' : 'rgba(237, 255, 0, 0.5)',
        boxShadow: isDark
          ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        height: '36px',
        whiteSpace: 'nowrap',
      }
    : {
        ...DESKTOP_HEADER_NAV_PILL_STYLE,
      };

  const glowAnimation = mobile
    ? {
        boxShadow: isDark
          ? [
              'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)',
              'inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 0 0 1px rgba(237, 255, 0, 0.15), 0 0 14px rgba(237, 255, 0, 0.16)',
              'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)',
            ]
          : [
              '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              '0 0 0 1px rgba(237, 255, 0, 0.16), 0 0 12px rgba(237, 255, 0, 0.18)',
              '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            ],
        borderColor: isDark
          ? ['rgba(237, 255, 0, 0.5)', 'rgba(237, 255, 0, 0.72)', 'rgba(237, 255, 0, 0.5)']
          : ['rgba(237, 255, 0, 0.5)', 'rgba(237, 255, 0, 0.78)', 'rgba(237, 255, 0, 0.5)'],
      }
    : {
        boxShadow: isDark
          ? [
              'none',
              '0 0 0 1px rgba(237, 255, 0, 0.14), 0 0 12px rgba(237, 255, 0, 0.14)',
              'none',
            ]
          : [
              'none',
              '0 0 0 1px rgba(237, 255, 0, 0.12), 0 0 10px rgba(237, 255, 0, 0.14)',
              'none',
            ],
        borderColor: isDark
          ? ['rgba(255, 255, 255, 0)', 'rgba(237, 255, 0, 0.35)', 'rgba(255, 255, 255, 0)']
          : ['rgba(255, 255, 255, 0)', 'rgba(76, 115, 15, 0.28)', 'rgba(255, 255, 255, 0)'],
      };

  const iconColorClass = mobile
    ? isDark
      ? 'text-primary'
      : 'text-[#1a1a1a]'
    : 'nav-text';

  return (
    <motion.button
      type="button"
      onClick={onClick}
      className={className}
      style={style}
      title="Start Here"
      aria-label="Start Here"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      animate={glowAnimation}
      transition={{
        duration: 2.8,
        ease: 'easeInOut',
        repeat: Infinity,
        repeatDelay: 4.8,
      }}
    >
      <span className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
          <FileText className={`w-3 h-3 ${iconColorClass}`} aria-hidden />
        </div>
        <span className={mobile ? 'text-[13px]' : 'hidden md:block nav-text'}>{mobile ? 'Start' : 'Start Here'}</span>
      </span>
    </motion.button>
  );
};

// Calendar Picker Component
const CalendarPicker = ({ selectedDate, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  const isToday = (day) => {
    const today = new Date();
    const currentDate = new Date(currentYear, currentMonth, day);
    return today.toDateString() === currentDate.toDateString();
  };
  
  const isSelected = (day) => {
    if (!selectedDate) return false;
    const selected = new Date(selectedDate);
    const currentDate = new Date(currentYear, currentMonth, day);
    return selected.toDateString() === currentDate.toDateString();
  };
  
  const handleDateClick = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    onDateSelect(date.toISOString().split('T')[0]);
  };
  
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };
  
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isCurrentDay = isToday(day);
      const isSelectedDay = isSelected(day);
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-8 w-8 rounded text-sm transition-colors ${
            isSelectedDay
              ? 'bg-primary text-primary-foreground'
              : isCurrentDay
              ? 'text-primary font-semibold'
              : 'text-foreground hover:bg-muted'
          }`}
          style={{ backgroundColor: isSelectedDay ? undefined : (isCurrentDay ? 'var(--input)' : 'var(--input)') }}
        >
          {day}
        </button>
      );
    }
    
    return days;
  };
  
  return (
    <div className="calendar-picker">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-1 hover:bg-muted rounded transition-colors"
                         style={{ backgroundColor: 'var(--input)' }}
        >
          <ChevronDown className="w-4 h-4 rotate-90 text-muted-foreground" />
        </button>
        
        <h3 className="text-sm font-semibold text-foreground">
          {months[currentMonth]} {currentYear}
        </h3>
        
        <button
          onClick={() => navigateMonth('next')}
          className="p-1 hover:bg-muted rounded transition-colors"
                         style={{ backgroundColor: 'var(--input)' }}
        >
          <ChevronDown className="w-4 h-4 -rotate-90 text-muted-foreground" />
        </button>
      </div>
      
      {/* Days of week */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map(day => (
          <div key={day} className="h-6 text-xs font-medium text-muted-foreground text-center">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {renderCalendarDays()}
      </div>
      
      {/* Quick actions */}
      <div className="mt-4 pt-3 border-t border-border">
        <div className="flex gap-2">
          <button
            onClick={() => {
              const today = new Date();
              onDateSelect(today.toISOString().split('T')[0]);
            }}
            className="flex-1 px-2 py-1 text-xs text-foreground rounded transition-colors hover:bg-muted"
                         style={{ backgroundColor: 'var(--input)' }}
          >
            Today
          </button>
          <button
            onClick={() => {
              const nextWeek = new Date();
              nextWeek.setDate(nextWeek.getDate() + 7);
              onDateSelect(nextWeek.toISOString().split('T')[0]);
            }}
            className="flex-1 px-2 py-1 text-xs text-foreground rounded transition-colors hover:bg-muted"
                         style={{ backgroundColor: 'var(--input)' }}
          >
            Next Week
          </button>
          <button
            onClick={() => {
              const thirtyDays = new Date();
              thirtyDays.setDate(thirtyDays.getDate() + 30);
              onDateSelect(thirtyDays.toISOString().split('T')[0]);
            }}
            className="flex-1 px-2 py-1 text-xs text-foreground rounded transition-colors hover:bg-muted"
                         style={{ backgroundColor: 'var(--input)' }}
          >
            30 Days
          </button>
        </div>
      </div>
    </div>
  );
};

// Theme Toggle Component
const AnimatedThemeToggle = () => {
  const { toggleTheme, isDark } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="group flex items-center space-x-2 px-3 py-2 rounded-[20px] text-sm font-medium transition-all duration-200 border border-transparent nav-text hover:border-white/30 focus:outline-none"
      style={{
        background: 'transparent',
        height: '36px',
        minWidth: 'fit-content',
        flexShrink: 0,
        whiteSpace: 'nowrap'
      }}
      title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
    >
      <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
        {isDark ? (
          <Sun className="w-3 h-3 nav-text" />
        ) : (
          <Moon className="w-3 h-3 nav-text" />
        )}
      </div>
      <span className="hidden lg:block nav-text">{isDark ? "Light" : "Dark"}</span>
    </button>
  );
};

const InvoiceDesignerContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { updateCurrentInvoice, updateCompanySettings, updateFormattedAddress } = useInvoice();
  const { settings: companySettings, loading: settingsLoading, isComplete: settingsComplete, formattedAddress } = useCompanySettings();
  const { getSupportedCurrencies, getCurrencySymbol, changeCurrency, selectedCurrency } = useCurrency();
  
  // Navigation state
  const [activeTab, setActiveTab] = useState('');
  
  // Set active tab based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/') {
      setActiveTab('/');
    }
  }, [location.pathname]);

  useEffect(() => {
    enqueueUsageEvent('page_view', {
      path: window.location.pathname,
      meta: {
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        lang: typeof navigator !== 'undefined' ? navigator.language || '' : '',
      },
    });
    const stopHb = startUsageHeartbeat(25000);
    return () => stopHb();
  }, []);

  // Navigation items - streamlined to invoice creation only
  const navigationTabs = [
    // Removed Create Invoice tab as requested
  ];
  
  // Form state
  const [formData, setFormData] = useState({
    customerId: null, // Add customerId field
    // Business Information
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZipCode: '',
    businessCountry: '',
    businessTaxId: '',
    businessWebsite: '',
    // Payment Details
    iban: '',
    bic: '',
    sortCode: '',
    accountNumber: '',
    paymentLink: '',
    // Customer Information
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerZipCode: '',
    customerCountry: '',
    dueDate: '',
    notes: '',
    currency: companySettings?.defaultCurrency || 'USD',
    lineItems: [
      { id: 1, description: 'Design Services', quantity: 1, rate: 0, taxRate: 0, amount: 0, notes: '' }
    ]
  });

  // UI state
  const [showPreview, setShowPreview] = useState(true);
  const [viewMode, setViewMode] = useState('desktop'); // desktop or mobile
  const [mobileTab, setMobileTab] = useState('edit'); // edit or preview for mobile
  const [mobileTabSlideDirection, setMobileTabSlideDirection] = useState(1);
  const [nextItemMode, setNextItemMode] = useState('hourly'); // controls mode for the next item to be added
  const [activeItemId, setActiveItemId] = useState(null); // tracks which item is active
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showJobCategoryDropdown, setShowJobCategoryDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDisplayOptionsDropdown, setShowDisplayOptionsDropdown] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationToastCollapsed, setValidationToastCollapsed] = useState(false);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const prevValidationErrorCountRef = useRef(0);
  const hasPlayedMobileEditIntroRef = useRef(false);

  useEffect(() => {
    const count = Object.values(validationErrors).filter((e) => e && String(e).trim() !== '').length;
    if (count > 0 && prevValidationErrorCountRef.current === 0) {
      setValidationToastCollapsed(false);
    }
    prevValidationErrorCountRef.current = count;
  }, [validationErrors]);

  useEffect(() => {
    if (viewMode === 'mobile' && mobileTab === 'edit' && !hasPlayedMobileEditIntroRef.current) {
      hasPlayedMobileEditIntroRef.current = true;
    }
  }, [viewMode, mobileTab]);
  const [showCustomOptions, setShowCustomOptions] = useState(false);
  // Left-column panels: start collapsed so the page loads compact; user expands as needed
  const [showBusinessInfo, setShowBusinessInfo] = useState(false);
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [showLineItems, setShowLineItems] = useState(false);
  const [selectedJobCategory, setSelectedJobCategory] = useState('Website Development');
  const currentTutorialStep = START_HERE_TUTORIAL_STEPS[tutorialStepIndex] || START_HERE_TUTORIAL_STEPS[0];

  const setActiveTutorialSection = (sectionId) => {
    setShowBusinessInfo(sectionId === 'business');
    setShowClientInfo(sectionId === 'client');
    setShowInvoiceDetails(sectionId === 'invoiceDetails');
    setShowPaymentDetails(sectionId === 'paymentDetails');
    setShowLineItems(sectionId === 'lineItems');
  };

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setIsTutorialActive(true);
    setValidationErrors({});
    setPaymentValidationErrors({
      iban: '',
      bic: '',
      sortCode: ''
    });
    setActiveTutorialSection('business');
    if (viewMode === 'mobile') {
      setMobileTab('edit');
    }
  };

  const cancelTutorial = () => {
    setIsTutorialActive(false);
    setTutorialStepIndex(0);
  };

  const goToTutorialStep = (nextIndex) => {
    const safeIndex = Math.min(Math.max(nextIndex, 0), START_HERE_TUTORIAL_STEPS.length - 1);
    const nextStep = START_HERE_TUTORIAL_STEPS[safeIndex];
    setTutorialStepIndex(safeIndex);
    setIsTutorialActive(true);
    setValidationErrors((prev) => ({ ...prev }));
    setActiveTutorialSection(nextStep.id);
    if (viewMode === 'mobile') {
      setMobileTab('edit');
    }
  };

  useEffect(() => {
    if (!isTutorialActive) {
      return;
    }

    if (viewMode === 'mobile' && mobileTab !== 'edit') {
      setMobileTab('edit');
    }

    setActiveTutorialSection(currentTutorialStep.id);

    const raf = requestAnimationFrame(() => {
      const section = document.querySelector(`[data-tutorial-section="${currentTutorialStep.id}"]`);
      if (section && typeof section.scrollIntoView === 'function') {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isTutorialActive, currentTutorialStep.id, mobileTab, viewMode]);
  
  // Payment validation errors
  const [paymentValidationErrors, setPaymentValidationErrors] = useState({
    iban: '',
    bic: '',
    sortCode: ''
  });
  const [customStyles, setCustomStyles] = useState({
    primaryColor: '#3B82F6',
    fontFamily: 'Inter',
    headerBackgroundColor: '#FFFFFF',
    showLogo: true,
    showCompanyAddress: true,
    showWebsite: true,
    showTaxNumber: true,
    showPaymentTerms: true,
    showNotes: true
  });
  const [uploadedLogo, setUploadedLogo] = useState(null);
  // Stable preview invoice tag (only for UI). Real number is assigned by backend on save
  const [previewInvoiceTag] = useState(() => `INV-${Math.random().toString(36).substr(2, 9).toUpperCase()}`);
  
  // Template management state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [templateConfig, setTemplateConfig] = useState(null);
  
  const mobileDownloadReady = useMemo(() => {
    const requiredFields = [
      'businessName', 'businessEmail', 'businessAddress', 'businessCity', 'businessState', 'businessZipCode',
      'customerName', 'customerEmail', 'customerAddress', 'customerCity', 'customerState', 'customerZipCode',
      'dueDate'
    ];

    const isValidWebsite = (website) => {
      if (!website || website.trim() === '') return true;
      try {
        const url = website.startsWith('http') ? website : `https://${website}`;
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };

    const hasRequiredFields = requiredFields.every((field) => {
      const value = formData[field];
      if (!value || value.toString().trim() === '') return false;
      if (field === 'businessEmail' || field === 'customerEmail') {
        return isMinimalInvoiceEmailValid(value);
      }
      return true;
    });

    const hasValidWebsite = isValidWebsite(formData.businessWebsite || '');
    const hasValidLineItems =
      formData.lineItems &&
      formData.lineItems.length > 0 &&
      formData.lineItems.every(
        (item) => item.description && item.description.trim() && item.quantity > 0 && item.rate > 0
      );

    return hasRequiredFields && hasValidWebsite && hasValidLineItems;
  }, [formData]);




  useEffect(() => {
    // Set default due date to 30 days from now
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + 30);
    
    // Use company settings currency as the primary currency
    const companyCurrency = companySettings?.defaultCurrency || 'USD';
    
    setFormData(prev => ({
      ...prev,
      dueDate: defaultDueDate.toISOString().split('T')[0],
      currency: companyCurrency
    }));
    
    // Sync the global currency context with company settings
    if (companyCurrency && companyCurrency !== selectedCurrency) {
      changeCurrency(companyCurrency);
    }
    
    // Set the first item as active by default
    setActiveItemId(1);
  }, [companySettings, changeCurrency, selectedCurrency]);

  // Payment validation handlers
  const validatePaymentField = (field, value) => {
    let validation = { isValid: true, error: '' };
    
    switch (field) {
      case 'iban':
        validation = validateIBAN(value);
        break;
      case 'bic':
        validation = validateBIC(value);
        break;
      case 'sortCode':
        validation = validateSortCode(value);
        break;
      default:
        break;
    }
    
    setPaymentValidationErrors(prev => ({
      ...prev,
      [field]: validation.error
    }));
    
    return validation.isValid;
  };

  const handlePaymentFieldChange = (field, value, formatter = null) => {
    // Format value if formatter provided
    const formattedValue = formatter ? formatter(value) : value;
    
    // Update form data
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    
    // Validate the field
    validatePaymentField(field, formattedValue);
  };

  // Auto-disable showLogo when no logo is available
  useEffect(() => {
    const hasLogo = uploadedLogo || companySettings?.logo;
    if (!hasLogo && customStyles.showLogo) {
      setCustomStyles(prev => ({
        ...prev,
        showLogo: false
      }));
    }
  }, [uploadedLogo, companySettings?.logo, customStyles.showLogo]);

  // Fetch available templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE}/api/pdf/templates`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            console.log('Templates loaded from API:', data.templates);
            setAvailableTemplates(data.templates);
          }
        } else {
          // Fallback templates if API fails
          setAvailableTemplates({
            modern: { name: 'Modern', description: 'Clean and contemporary design' },
            clean: { name: 'Clean', description: 'Spacious and elegant design' }
          });
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        console.log('Using fallback templates:', {
          modern: { name: 'Modern', description: 'Clean and contemporary design' },
          clean: { name: 'Clean', description: 'Spacious and elegant design' }
        });
        // Fallback templates if fetch fails
        setAvailableTemplates({
          modern: { name: 'Modern', description: 'Clean and contemporary design' },
          clean: { name: 'Clean', description: 'Spacious and elegant design' }
        });
      }
    };

    fetchTemplates();
  }, []);

  // Fetch template configuration when template changes
  useEffect(() => {
    const fetchTemplateConfig = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE}/api/pdf/template/config?template=${selectedTemplate}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setTemplateConfig(data.config);
          }
        }
      } catch (error) {
        console.error('Failed to fetch template config:', error);
      }
    };

    if (selectedTemplate) {
      fetchTemplateConfig();
    }
  }, [selectedTemplate]);

  // Click outside handler to minimize expanded line items
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the line items section
      const lineItemsSection = document.querySelector('[data-line-items-section]');
      if (lineItemsSection && !lineItemsSection.contains(event.target)) {
        setActiveItemId(null);
      }
    };

    // Add event listener when there's an active item
    if (activeItemId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeItemId]);

  // Template navigation functions
  const getTemplateNames = () => {
    const names = Object.keys(availableTemplates);
    console.log('Available template names:', names);
    return names;
  };

  const getCurrentTemplateIndex = () => {
    const templateNames = getTemplateNames();
    return templateNames.indexOf(selectedTemplate);
  };

  const navigateTemplate = (direction) => {
    const templateNames = getTemplateNames();
    const currentIndex = getCurrentTemplateIndex();
    
    console.log('Template navigation:', { direction, templateNames, currentIndex, selectedTemplate });
    
    if (direction === 'prev') {
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : templateNames.length - 1;
      setSelectedTemplate(templateNames[prevIndex]);
    } else if (direction === 'next') {
      const nextIndex = currentIndex < templateNames.length - 1 ? currentIndex + 1 : 0;
      setSelectedTemplate(templateNames[nextIndex]);
    }
  };

  // Update invoice context whenever invoice data changes
  useEffect(() => {
    if (companySettings) {
      const invoiceData = convertToInvoiceFormat();
      updateCurrentInvoice(invoiceData);
      updateCompanySettings(companySettings);
      updateFormattedAddress(formattedAddress);
    }
  }, [formData, companySettings, formattedAddress, updateCurrentInvoice, updateCompanySettings, updateFormattedAddress]);

  // Responsive layout logic
  const isMobile = window.innerWidth < 1024;
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      if (mobile && viewMode === 'desktop') {
        setViewMode('mobile');
      } else if (!mobile && viewMode === 'mobile') {
        setViewMode('desktop');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey)) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleDownloadPDF();
        } else if (e.key === 'p') {
          e.preventDefault();
          setShowPreview(!showPreview);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPreview]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-currency-dropdown]')) {
        setShowCurrencyDropdown(false);
      }
      if (!event.target.closest('[data-job-category-dropdown]')) {
        setShowJobCategoryDropdown(false);
      }
      if (!event.target.closest('[data-date-picker]')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = formData.lineItems
      .reduce((sum, item) => {
        const itemTaxRate = item.taxRate || 0;
        return sum + (item.amount * (itemTaxRate / 100));
      }, 0);
    const total = subtotal + taxAmount;
    
    return { subtotal, taxAmount, total };
  };

  // Line item handlers
  const addLineItem = () => {
    const newId = Math.max(...formData.lineItems.map(item => item.id)) + 1;
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { 
        id: newId, 
        description: 'New Service', 
        quantity: 1, 
        rate: 0, 
        taxRate: 0, 
        amount: 0,
        notes: ''
      }]
    }));
    // Make the new item active
    setActiveItemId(newId);
  };

  const removeLineItem = (id) => {
    if (formData.lineItems.length > 1) {
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter(item => item.id !== id)
      }));
      // If we're removing the active item, clear active state
      if (activeItemId === id) {
        setActiveItemId(null);
      }
    }
  };

  const updateLineItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate amount
          if (field === 'quantity' || field === 'rate') {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    }));
    
    // Clear line items validation errors when user starts typing
    if (validationErrors.lineItems || validationErrors.subtotal || validationErrors.total) {
      setValidationErrors(prev => ({
      ...prev,
        lineItems: '',
        subtotal: '',
        total: ''
      }));
    }
  };

  const MAX_TEXT_FIELD_LENGTH = 45;
  const LIMITED_TEXT_FIELDS = [
    'businessName',
    'businessEmail',
    'businessWebsite',
    'businessTaxId',
    'businessAddress',
    'customerName',
    'customerEmail',
    'customerAddress'
  ];

  const sanitizeFieldValue = (field, value) => {
    if (LIMITED_TEXT_FIELDS.includes(field) && typeof value === 'string') {
      return value.slice(0, MAX_TEXT_FIELD_LENGTH);
    }
    return value;
  };

  // Business handlers
  const updateBusinessField = (field, value) => {
    const nextValue = sanitizeFieldValue(field, value);
    setFormData(prev => ({ ...prev, [field]: nextValue }));

    if (field === 'businessEmail') {
      setValidationErrors(prev => ({
        ...prev,
        [field]: nextValue.trim() && !isMinimalInvoiceEmailValid(nextValue) ? 'Email Invalid' : ''
      }));
    } else if (validationErrors[field]) {
      // Clear validation error when user starts typing
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
  };

  // Customer handlers
  const updateCustomerField = (field, value) => {
    const nextValue = sanitizeFieldValue(field, value);
    setFormData(prev => ({ ...prev, [field]: nextValue }));

    if (field === 'customerEmail') {
      setValidationErrors(prev => ({
        ...prev,
        [field]: nextValue.trim() && !isMinimalInvoiceEmailValid(nextValue) ? 'Email Invalid' : ''
      }));
    } else if (validationErrors[field]) {
      // Clear validation error when user starts typing
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };



  // Helper function to load scripts from CDN
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
      return;
    }

      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const collectInvoiceValidationErrors = () => {
    const errors = {};

    if (!formData.businessName || !formData.businessName.trim()) {
      errors.businessName = 'Business name is required';
    }

    if (!formData.businessAddress || !formData.businessAddress.trim()) {
      errors.businessAddress = 'Business address is required';
    }

    if (!formData.businessCity || !formData.businessCity.trim()) {
      errors.businessCity = 'Business city is required';
    }

    if (!formData.businessState || !formData.businessState.trim()) {
      errors.businessState = 'Business state is required';
    }

    if (!formData.businessZipCode || !formData.businessZipCode.trim()) {
      errors.businessZipCode = 'Business postal code is required';
    }

    if (formData.businessWebsite && formData.businessWebsite.trim()) {
      const website = formData.businessWebsite.trim();
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
      if (!urlPattern.test(website)) {
        errors.businessWebsite = 'Please enter a valid website URL (e.g., https://www.example.com)';
      }
    }

    if (!formData.customerName || !formData.customerName.trim()) {
      errors.customerName = 'Customer name is required';
    }

    if (!formData.businessEmail || !formData.businessEmail.trim()) {
      errors.businessEmail = 'Business email is required';
    } else if (!isMinimalInvoiceEmailValid(formData.businessEmail)) {
      errors.businessEmail = 'Email Invalid';
    }

    if (!formData.customerEmail || !formData.customerEmail.trim()) {
      errors.customerEmail = 'Customer email is required';
    } else if (!isMinimalInvoiceEmailValid(formData.customerEmail)) {
      errors.customerEmail = 'Email Invalid';
    }

    if (!formData.customerAddress || !formData.customerAddress.trim()) {
      errors.customerAddress = 'Customer address is required';
    }

    if (!formData.customerCity || !formData.customerCity.trim()) {
      errors.customerCity = 'Customer city is required';
    }

    if (!formData.customerState || !formData.customerState.trim()) {
      errors.customerState = 'Customer state is required';
    }

    if (!formData.customerZipCode || !formData.customerZipCode.trim()) {
      errors.customerZipCode = 'Customer postal code is required';
    }

    if (!formData.dueDate || !formData.dueDate.trim()) {
      errors.dueDate = 'Due date is required';
    }

    if (formData.lineItems.length === 0) {
      errors.lineItems = 'At least one line item is required';
    } else {
      const emptyItems = formData.lineItems.filter(item => !item.description || !item.description.trim());
      if (emptyItems.length > 0) {
        errors.lineItems = 'All line items must have descriptions';
      }

      const zeroQuantityItems = formData.lineItems.filter(item => item.quantity <= 0);
      if (zeroQuantityItems.length > 0) {
        errors.lineItems = 'All items must have valid quantities greater than zero';
      }

      const zeroRateItems = formData.lineItems.filter(item => item.rate <= 0);
      if (zeroRateItems.length > 0) {
        errors.lineItems = 'All items must have valid unit prices greater than zero';
      }

      const invalidAmountItems = formData.lineItems.filter(item =>
        item.amount !== (item.quantity * item.rate)
      );
      if (invalidAmountItems.length > 0) {
        errors.lineItems = 'Line item totals are not calculated correctly';
      }

      const calculatedSubtotal = formData.lineItems.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
      if (calculatedSubtotal <= 0) {
        errors.subtotal = 'Subtotal must be greater than zero';
      }
    }

    return Object.fromEntries(
      Object.entries(errors).filter(([, error]) => error && error.trim() !== '')
    );
  };

  const clearTutorialStepErrors = (stepId) => {
    if (stepId === 'paymentDetails') {
      setPaymentValidationErrors({
        iban: '',
        bic: '',
        sortCode: ''
      });
      return;
    }

    const keysToClear = TUTORIAL_SECTION_ERROR_KEYS[stepId] || [];
    setValidationErrors(prev =>
      Object.fromEntries(
        Object.entries(prev).filter(([field]) => !keysToClear.includes(field))
      )
    );
  };

  const validatePaymentTutorialStep = () => {
    const nextErrors = {
      iban: formData.iban && formData.iban.trim() ? validateIBAN(formData.iban).error : '',
      bic: formData.bic && formData.bic.trim() ? validateBIC(formData.bic).error : '',
      sortCode: formData.sortCode && formData.sortCode.trim() ? validateSortCode(formData.sortCode).error : ''
    };

    setPaymentValidationErrors(nextErrors);
    return Object.values(nextErrors).every((error) => !error);
  };

  const validateTutorialStep = (stepId) => {
    if (stepId === 'paymentDetails') {
      return validatePaymentTutorialStep();
    }

    const allErrors = collectInvoiceValidationErrors();
    const keysForSection = TUTORIAL_SECTION_ERROR_KEYS[stepId] || [];
    const sectionErrors = Object.fromEntries(
      Object.entries(allErrors).filter(([field]) => keysForSection.includes(field))
    );

    setValidationErrors(sectionErrors);
    return Object.keys(sectionErrors).length === 0;
  };

  const handleTutorialNext = () => {
    if (!isTutorialActive) {
      return;
    }

    if (!validateTutorialStep(currentTutorialStep.id)) {
      return;
    }

    if (tutorialStepIndex === START_HERE_TUTORIAL_STEPS.length - 1) {
      cancelTutorial();
      return;
    }

    goToTutorialStep(tutorialStepIndex + 1);
  };

  const handleTutorialSkip = () => {
    if (!isTutorialActive) {
      return;
    }

    clearTutorialStepErrors(currentTutorialStep.id);

    if (tutorialStepIndex === START_HERE_TUTORIAL_STEPS.length - 1) {
      cancelTutorial();
      return;
    }

    goToTutorialStep(tutorialStepIndex + 1);
  };

  const renderTutorialGuide = (sectionId) => {
    if (!isTutorialActive || currentTutorialStep.id !== sectionId) {
      return null;
    }

    return (
      <StartHereGuide
        step={currentTutorialStep}
        currentStepIndex={tutorialStepIndex}
        totalSteps={START_HERE_TUTORIAL_STEPS.length}
        onNext={handleTutorialNext}
        onSkip={handleTutorialSkip}
        onCancel={cancelTutorial}
        isLastStep={tutorialStepIndex === START_HERE_TUTORIAL_STEPS.length - 1}
        isDark={isDark}
      />
    );
  };

  const getTutorialSectionClassName = (baseClassName, sectionId) =>
    `${baseClassName}${isTutorialActive && currentTutorialStep.id === sectionId ? ' border-primary/40 shadow-lg shadow-primary/10' : ''}`;

  const handleSectionToggle = (sectionId) => {
    if (isTutorialActive) {
      return;
    }

    switch (sectionId) {
      case 'business':
        setShowBusinessInfo(!showBusinessInfo);
        break;
      case 'client':
        setShowClientInfo(!showClientInfo);
        break;
      case 'invoiceDetails':
        setShowInvoiceDetails(!showInvoiceDetails);
        break;
      case 'paymentDetails':
        setShowPaymentDetails(!showPaymentDetails);
        break;
      case 'lineItems':
        setShowLineItems(!showLineItems);
        break;
      default:
        break;
    }
  };


  // Download PDF functionality
  const handleDownloadPDF = async () => {
    // Clear previous validation errors and messages
    setValidationErrors({});
    const filteredErrors = collectInvoiceValidationErrors();
    
    // Set validation errors
    setValidationErrors(filteredErrors);
    
    // If there are validation errors, show them and don't proceed
    if (Object.keys(filteredErrors).length > 0) {
      // Always expand toast when download is blocked by validation
      setValidationToastCollapsed(false);
      console.log('❌ Validation errors found:', filteredErrors);
      return;
    }

    setGeneratingPDF(true);
    try {
      enqueueUsageEvent('pdf_attempt', { path: window.location.pathname });
      await flushAnalyticsQueue();

      // Calculate totals for PDF generation
      const totals = calculateTotals();
      
      // Prepare invoice data for backend PDF generation
      const invoiceData = {
        invoice_number: previewInvoiceTag,
        issue_date: formData.issueDate || new Date().toISOString().split('T')[0],
        due_date: formData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'Draft',
        notes: formData.notes || '',
        tax_rate: totals.taxAmount > 0 ? ((totals.taxAmount / totals.subtotal) * 100) : 0,
        customer_name: formData.customerName || '',
        customer_email: formData.customerEmail || '',
        customer_phone: formData.customerPhone || '',
        customer_address: formData.customerAddress || '',
        customer_city: formData.customerCity || '',
        customer_state: formData.customerState || '',
        customer_zip_code: formData.customerZipCode || '',
        customer_country: formData.customerCountry || '',
        items: formData.lineItems.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          price: item.rate || 0, // Support both 'rate' and 'price' for backend compatibility
          amount: item.amount || 0,
          total: item.amount || 0, // Support both 'amount' and 'total' for backend compatibility
          tax_rate: item.taxRate || 0, // Include individual item tax rate
          tax_percentage: item.taxRate || 0, // Include tax percentage for PDF templates
          tax_amount: (item.amount || 0) * ((item.taxRate || 0) / 100), // Calculate tax amount for this item
          notes: item.notes || ''
        })),
        payment_details: {
          iban: formData.iban || '',
          bic: formData.bic || '',
          sort_code: formData.sortCode || '',
          account_number: formData.accountNumber || '',
          payment_link: formData.paymentLink || ''
        }
      };

      // Prepare company data from form
      const companyData = {
        name: formData.businessName || 'Your Company',
        email: formData.businessEmail || '',
        phone: formData.businessPhone || '',
        website: formData.businessWebsite || '',
        address: formData.businessAddress || '', // Changed from address_line1 to address
        city: formData.businessCity || '',
        state: formData.businessState || '',
        zip_code: formData.businessZipCode || '', // Changed from postal_code to zip_code
        country: formData.businessCountry || '',
        currency: formData.currency || 'USD',
        logo_url: uploadedLogo || companySettings?.logo || null,
        tax_id: formData.businessTaxId || ''
      };

      // Prepare customization data
      const customizationData = {
        primary_color: customStyles.primaryColor || '#edff00',
        font_family: customStyles.fontFamily ? `${customStyles.fontFamily}, sans-serif` : 'Arial, sans-serif',
        header_background_color: customStyles.headerBackgroundColor || '#edff00',
        show_logo: customStyles.showLogo !== false,
        show_tax_number: customStyles.showTaxNumber !== false,
        show_payment_terms: customStyles.showPaymentTerms !== false,
        show_notes: customStyles.showNotes !== false,
        show_item_codes: customStyles.showItemCodes || false,
        show_company_address: true, // Always show company address
        show_website: customStyles.showWebsite !== false,
        show_contact_info: true // Always show contact info
      };

      // Debug font family
      console.log('🔤 Font Family Debug:', {
        customStylesFontFamily: customStyles.fontFamily,
        customizationDataFontFamily: customizationData.font_family,
        selectedTemplate: selectedTemplate
      });

      // Call backend PDF generation service
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/pdf/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          invoice_data: invoiceData,
          company_data: companyData,
          customization: customizationData,
          template: selectedTemplate,
          analytics: getAnalyticsContext(),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const result = await response.json();
      
      // Download the PDF
      const downloadResponse = await fetch(`${API_BASE}/api/pdf/download?pdf_path=${encodeURIComponent(result.pdf_path)}`);
      
      if (!downloadResponse.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invoice-' + (formData.customerName?.replace(/[^a-zA-Z0-9]/g, '-') || 'untitled') + '-' + new Date().toISOString().split('T')[0] + '.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF: ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Update custom style
  const updateCustomStyle = (key, value) => {
    setCustomStyles(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedLogo(e.target.result);
        // Auto-enable showLogo when logo is uploaded
        setCustomStyles(prev => ({
          ...prev,
          showLogo: true
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeUploadedLogo = () => {
    setUploadedLogo(null);
    // Auto-disable showLogo when logo is removed
    setCustomStyles(prev => ({
      ...prev,
      showLogo: false
    }));
  };

  // Helper function to convert hex to RGBA
  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  // Update currency
  const updateCurrency = (newCurrency) => {
    setFormData(prev => ({ ...prev, currency: newCurrency }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, dueDate: date }));
    setShowDatePicker(false);
  };


  // Calendar helper functions
  const formatDateForDisplay = (dateString) => {
    return formatDate(dateString, 'display');
  };

  // Job categories and their common work item templates
  const jobTemplates = {
    'Website Development': [
      { description: 'Frontend Development', rate: 85, quantity: 1 },
      { description: 'Backend Development', rate: 90, quantity: 1 },
      { description: 'Database Setup', rate: 75, quantity: 1 },
      { description: 'Responsive Design', rate: 80, quantity: 1 },
      { description: 'API Integration', rate: 95, quantity: 1 },
      { description: 'Testing & QA', rate: 70, quantity: 1 },
      { description: 'Website Maintenance', rate: 60, quantity: 1 },
      { description: 'Performance Optimisation', rate: 100, quantity: 1 }
    ],
    'Digital Marketing & SEO': [
      { description: 'SEO Audit', rate: 120, quantity: 1 },
      { description: 'Keyword Research', rate: 80, quantity: 1 },
      { description: 'Content Optimisation', rate: 75, quantity: 1 },
      { description: 'Link Building Campaign', rate: 100, quantity: 1 },
      { description: 'Google Ads Management', rate: 150, quantity: 1 },
      { description: 'Analytics Setup', rate: 90, quantity: 1 },
      { description: 'Social Media Marketing', rate: 85, quantity: 1 },
      { description: 'Marketing Strategy', rate: 200, quantity: 1 }
    ],
    'Graphic Design': [
      { description: 'Logo Design', rate: 300, quantity: 1 },
      { description: 'Brand Identity Package', rate: 800, quantity: 1 },
      { description: 'Business Card Design', rate: 150, quantity: 1 },
      { description: 'Brochure Design', rate: 250, quantity: 1 },
      { description: 'Social Media Graphics', rate: 100, quantity: 1 },
      { description: 'Web Graphics', rate: 120, quantity: 1 },
      { description: 'Print Design', rate: 180, quantity: 1 },
      { description: 'Illustration', rate: 200, quantity: 1 }
    ],
    'Content Writing & Copywriting': [
      { description: 'Blog Article Writing', rate: 80, quantity: 1 },
      { description: 'Website Copywriting', rate: 100, quantity: 1 },
      { description: 'Product Descriptions', rate: 60, quantity: 1 },
      { description: 'Email Marketing Copy', rate: 90, quantity: 1 },
      { description: 'Social Media Content', rate: 70, quantity: 1 },
      { description: 'Sales Page Copy', rate: 150, quantity: 1 },
      { description: 'Content Strategy', rate: 120, quantity: 1 },
      { description: 'Editing & Proofreading', rate: 50, quantity: 1 }
    ],
    'Social Media Management': [
      { description: 'Content Creation', rate: 80, quantity: 1 },
      { description: 'Social Media Strategy', rate: 150, quantity: 1 },
      { description: 'Community Management', rate: 60, quantity: 1 },
      { description: 'Social Media Analytics', rate: 90, quantity: 1 },
      { description: 'Paid Social Campaigns', rate: 120, quantity: 1 },
      { description: 'Influencer Outreach', rate: 100, quantity: 1 },
      { description: 'Content Calendar', rate: 70, quantity: 1 },
      { description: 'Brand Monitoring', rate: 65, quantity: 1 }
    ],
    'Mobile App Development': [
      { description: 'iOS App Development', rate: 120, quantity: 1 },
      { description: 'Android App Development', rate: 115, quantity: 1 },
      { description: 'Cross-Platform Development', rate: 110, quantity: 1 },
      { description: 'App UI/UX Design', rate: 95, quantity: 1 },
      { description: 'Backend API Development', rate: 100, quantity: 1 },
      { description: 'App Testing', rate: 80, quantity: 1 },
      { description: 'App Store Optimisation', rate: 90, quantity: 1 },
      { description: 'App Maintenance', rate: 75, quantity: 1 }
    ],
    'UI/UX Design': [
    { description: 'UX Research & Analysis', rate: 85, quantity: 1 },
    { description: 'Wireframing & Prototyping', rate: 75, quantity: 1 },
    { description: 'UI Design & Mockups', rate: 80, quantity: 1 },
    { description: 'User Testing & Feedback', rate: 90, quantity: 1 },
    { description: 'Design System Creation', rate: 95, quantity: 1 },
    { description: 'Responsive Design', rate: 85, quantity: 1 },
    { description: 'Design Review & Iteration', rate: 70, quantity: 1 },
    { description: 'Client Consultation', rate: 100, quantity: 1 }
    ],
    'Video Editing & Production': [
      { description: 'Video Editing', rate: 75, quantity: 1 },
      { description: 'Motion Graphics', rate: 90, quantity: 1 },
      { description: 'Colour Correction', rate: 60, quantity: 1 },
      { description: 'Audio Editing', rate: 50, quantity: 1 },
      { description: 'Video Production', rate: 150, quantity: 1 },
      { description: 'Animation', rate: 120, quantity: 1 },
      { description: 'Video Optimisation', rate: 40, quantity: 1 },
      { description: 'Thumbnail Design', rate: 35, quantity: 1 }
    ],
    'Accounting': [
      { description: 'Bookkeeping Services', rate: 40, quantity: 1 },
      { description: 'Financial Reporting', rate: 80, quantity: 1 },
      { description: 'Tax Preparation', rate: 100, quantity: 1 },
      { description: 'Payroll Processing', rate: 50, quantity: 1 },
      { description: 'Accounts Payable/Receivable', rate: 45, quantity: 1 },
      { description: 'Budget Planning', rate: 90, quantity: 1 },
      { description: 'Financial Analysis', rate: 120, quantity: 1 },
      { description: 'QuickBooks Setup', rate: 75, quantity: 1 }
    ],
    'Email Marketing': [
      { description: 'Email Campaign Setup', rate: 80, quantity: 1 },
      { description: 'Email Template Design', rate: 100, quantity: 1 },
      { description: 'List Management', rate: 50, quantity: 1 },
      { description: 'A/B Testing', rate: 70, quantity: 1 },
      { description: 'Email Automation', rate: 120, quantity: 1 },
      { description: 'Newsletter Creation', rate: 60, quantity: 1 },
      { description: 'Email Analytics', rate: 65, quantity: 1 },
      { description: 'Deliverability Optimization', rate: 90, quantity: 1 }
    ],
    'Legal Consulting': [
      { description: 'Legal Consultation', rate: 300, quantity: 1 },
      { description: 'Contract Review', rate: 250, quantity: 1 },
      { description: 'Document Drafting', rate: 200, quantity: 1 },
      { description: 'Legal Research', rate: 150, quantity: 1 },
      { description: 'Compliance Review', rate: 280, quantity: 1 },
      { description: 'Intellectual Property', rate: 350, quantity: 1 },
      { description: 'Business Formation', rate: 400, quantity: 1 },
      { description: 'Legal Analysis', rate: 180, quantity: 1 }
    ]
  };

  // Get current work item templates based on selected job category
  const workItemTemplates = jobTemplates[selectedJobCategory] || jobTemplates['Website Development'];

  // Add template item
  const addTemplateItem = (template) => {
    const newId = Math.max(...formData.lineItems.map(item => item.id)) + 1;
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { 
        id: newId, 
        description: template.description, 
        quantity: template.quantity, 
        rate: template.rate, 
        taxRate: 0, 
        amount: template.quantity * template.rate,
        notes: ''
      }]
    }));
  };

  // Helper function to format business address from form data
  const getFormattedBusinessAddress = () => {
    const lines = [];
    
    // First line: Address, State
    const firstLineParts = [];
    if (formData.businessAddress?.trim()) {
      firstLineParts.push(formData.businessAddress.trim());
    }
    if (formData.businessState?.trim()) {
      firstLineParts.push(formData.businessState.trim());
    }
    if (firstLineParts.length > 0) {
      lines.push(firstLineParts.join(', '));
    }
    
    // Second line: City, Country, Postcode
    const secondLineParts = [];
    if (formData.businessCity?.trim()) {
      secondLineParts.push(formData.businessCity.trim());
    }
    if (formData.businessCountry?.trim()) {
      secondLineParts.push(formData.businessCountry.trim());
    }
    if (formData.businessZipCode?.trim()) {
      secondLineParts.push(formData.businessZipCode.trim());
    }
    if (secondLineParts.length > 0) {
      lines.push(secondLineParts.join(', '));
    }
    
    return lines.join('\n');
  };

  // Convert form data to Invoice format for live preview
  const convertToInvoiceFormat = () => {
    const totals = calculateTotals();
    
    return {
      invoiceNumber: previewInvoiceTag,
      issueDate: new Date(),
      dueDate: new Date(formData.dueDate),
      status: 'draft',
      company: {
        name: formData.businessName || 'Your Company',
        email: formData.businessEmail || '',
        phone: formData.businessPhone || '',
        website: formData.businessWebsite || '',
        taxId: formData.businessTaxId || '',
        address: {
          street: formData.businessAddress || '',
          city: formData.businessCity || '',
          state: formData.businessState || '',
          zipCode: formData.businessZipCode || '',
          country: formData.businessCountry || ''
        },
        formattedAddress: getFormattedBusinessAddress()
      },
      client: {
        name: formData.customerName || '',
        email: formData.customerEmail || '',
        phone: formData.customerPhone || '',
        address: {
          street: formData.customerAddress || '',
        city: formData.customerCity || '',
        state: formData.customerState || '',
        zipCode: formData.customerZipCode || '',
        country: formData.customerCountry || ''
        }
      },
      items: formData.lineItems.map(item => ({
        id: item.id.toString(),
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        amount: item.amount,
        notes: item.notes || '',
        taxRate: item.taxRate || 0
      })),
      totals: {
        subtotal: totals.subtotal,
        taxRate: 10, // Default 10% tax rate
        taxAmount: totals.taxAmount,
        discountRate: 0,
        discountAmount: 0,
        total: totals.total
      },
      paymentTerms: 30,
      currency: formData.currency,
      paymentInstructions: '',
      notes: formData.notes || '',
      paymentDetails: {
        iban: formData.iban || '',
        bic: formData.bic || '',
        sortCode: formData.sortCode || '',
        accountNumber: formData.accountNumber || '',
        paymentLink: formData.paymentLink || ''
      }
    };
  };

  const totals = calculateTotals();

  if (settingsLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col">
          {/* Page Header Skeleton */}
          <div className="mb-1 mt-2 px-4 sm:px-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-4 sm:ml-8">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-muted rounded-full animate-pulse"></div>
                  <div className="ml-2 sm:ml-3">
                    <div className="h-5 sm:h-6 w-24 sm:w-32 bg-muted rounded animate-pulse"></div>
                    <div className="h-3 sm:h-4 w-16 sm:w-24 bg-muted rounded mt-1 animate-pulse"></div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
                <div className="hidden sm:block h-8 w-8 bg-muted rounded animate-pulse"></div>
                <div className="hidden sm:block h-8 w-8 bg-muted rounded animate-pulse"></div>
                <div className="h-8 w-20 sm:w-32 bg-muted rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Main Content Skeleton */}
          <div className="flex-1 flex flex-col sm:flex-row overflow-visible mt-0">
            {/* Left Panel Skeleton - Form Editor */}
            <div className="w-full sm:w-1/2 overflow-y-auto">
              <div className="p-4">
                <div className="space-y-4">
                  {/* Business Information Skeleton */}
                  <div className="bg-card border border-border rounded-xl animate-pulse">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-muted rounded"></div>
                        <div className="h-6 w-40 bg-muted rounded"></div>
                      </div>
                      <div className="w-5 h-5 bg-muted rounded"></div>
                    </div>
                    <div className="px-4 pb-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="h-4 w-20 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                        <div>
                          <div className="h-4 w-24 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <div className="h-4 w-16 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                        <div>
                          <div className="h-4 w-12 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <div className="h-4 w-20 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Client Information Skeleton */}
                  <div className="bg-card border border-border rounded-xl animate-pulse">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-muted rounded"></div>
                        <div className="h-6 w-36 bg-muted rounded"></div>
                      </div>
                      <div className="w-5 h-5 bg-muted rounded"></div>
                    </div>
                    <div className="px-4 pb-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="h-4 w-20 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                        <div>
                          <div className="h-4 w-24 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                      </div>
                      <div>
                        <div className="h-4 w-16 bg-muted rounded mb-2"></div>
                        <div className="h-10 bg-muted rounded"></div>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Details Skeleton */}
                  <div className="bg-card border border-border rounded-xl animate-pulse">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-muted rounded"></div>
                        <div className="h-6 w-32 bg-muted rounded"></div>
                      </div>
                      <div className="w-5 h-5 bg-muted rounded"></div>
                    </div>
                    <div className="px-4 pb-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="h-4 w-24 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                        <div>
                          <div className="h-4 w-20 bg-muted rounded mb-2"></div>
                          <div className="h-10 bg-muted rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Line Items Skeleton */}
                  <div className="bg-card border border-border rounded-xl animate-pulse">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-muted rounded"></div>
                        <div className="h-6 w-28 bg-muted rounded"></div>
                      </div>
                      <div className="w-5 h-5 bg-muted rounded"></div>
                    </div>
                    <div className="px-4 pb-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-32 bg-muted rounded"></div>
                        <div className="h-8 w-24 bg-muted rounded"></div>
                      </div>
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex flex-col sm:grid sm:grid-cols-12 gap-3 sm:gap-2 p-3 bg-secondary rounded-lg">
                            {/* Description - full width on mobile */}
                            <div className="w-full sm:col-span-5 h-10 bg-muted rounded"></div>
                            {/* Qty, Rate, VAT in a row on mobile */}
                            <div className="flex sm:contents gap-2">
                              <div className="flex-1 sm:col-span-2 h-10 bg-muted rounded"></div>
                              <div className="flex-1 sm:col-span-2 h-10 bg-muted rounded"></div>
                              <div className="flex-1 sm:col-span-2 h-10 bg-muted rounded"></div>
                              <div className="w-8 sm:col-span-1 h-10 bg-muted rounded"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Notes Skeleton */}
                  <div className="bg-card border border-border rounded-xl animate-pulse">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-muted rounded"></div>
                        <div className="h-6 w-16 bg-muted rounded"></div>
                      </div>
                      <div className="w-5 h-5 bg-muted rounded"></div>
                    </div>
                    <div className="px-4 pb-4">
                      <div className="h-20 bg-muted rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel Skeleton - Live Preview (hidden on mobile) */}
            <div className="hidden sm:block w-1/2 overflow-y-auto">
              <div className="h-full bg-card p-6 overflow-y-auto rounded-t-lg">
                {/* Preview Controls Skeleton */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="h-6 w-32 bg-muted rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>
                  </div>
                </div>

                {/* Invoice Preview Skeleton */}
                <div className="bg-white border border-border rounded-lg shadow-lg p-8 animate-pulse">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-8">
                    <div className="space-y-2">
                      <div className="h-6 w-32 bg-gray-300 rounded"></div>
                      <div className="h-4 w-24 bg-gray-300 rounded"></div>
                      <div className="h-4 w-28 bg-gray-300 rounded"></div>
                    </div>
                    <div className="text-right space-y-2">
                      <div className="h-8 w-24 bg-gray-300 rounded"></div>
                      <div className="h-4 w-20 bg-gray-300 rounded"></div>
                      <div className="h-4 w-16 bg-gray-300 rounded"></div>
                    </div>
                  </div>

                  {/* Client Details */}
                  <div className="mb-8 space-y-2">
                    <div className="h-5 w-16 bg-gray-300 rounded"></div>
                    <div className="h-4 w-32 bg-gray-300 rounded"></div>
                    <div className="h-4 w-28 bg-gray-300 rounded"></div>
                  </div>

                  {/* Line Items Table */}
                  <div className="mb-8">
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="h-4 w-20 bg-gray-300 rounded"></div>
                      <div className="h-4 w-12 bg-gray-300 rounded"></div>
                      <div className="h-4 w-16 bg-gray-300 rounded"></div>
                      <div className="h-4 w-16 bg-gray-300 rounded"></div>
                    </div>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="grid grid-cols-4 gap-4 mb-2">
                        <div className="h-4 w-full bg-gray-300 rounded"></div>
                        <div className="h-4 w-8 bg-gray-300 rounded"></div>
                        <div className="h-4 w-12 bg-gray-300 rounded"></div>
                        <div className="h-4 w-16 bg-gray-300 rounded"></div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <div className="h-4 w-16 bg-gray-300 rounded"></div>
                      <div className="h-4 w-20 bg-gray-300 rounded"></div>
                    </div>
                    <div className="flex justify-between">
                      <div className="h-4 w-12 bg-gray-300 rounded"></div>
                      <div className="h-4 w-16 bg-gray-300 rounded"></div>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <div className="h-6 w-16 bg-gray-300 rounded"></div>
                      <div className="h-6 w-24 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen flex flex-col">
        <h1 className="sr-only">
          Free invoice generator and online invoice maker for freelancers, agencies,
          consultants, and small businesses
        </h1>
        {/* Page Header — stack brand + actions on narrow viewports */}
        <div className="mb-1 mt-2 px-2 sm:px-4 md:px-0">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-3">
            <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:gap-4 md:ml-6 lg:ml-8">
              <div className="flex items-center justify-between gap-2 md:justify-start md:gap-3">
                <div className="flex min-w-0 flex-1 items-center">
                  <div className="flex items-center min-w-0">
                    {/* Mobile: Logo + Header with invoice number below */}
                    <div className="md:hidden flex min-w-0 items-start space-x-3">
                      <div 
                        className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center mt-1"
                        style={{
                          background: 'rgba(167, 238, 67, 0.15)',
                          border: '1px solid rgba(167, 238, 67, 0.3)'
                        }}
                      >
                        <span 
                          className="font-medium text-xs"
                          style={{ 
                            color: 'var(--primary)'
                          }}
                        >
                          invo
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg font-bold text-foreground truncate">Create Invoice</p>
                        <p className="text-xs text-muted-foreground truncate">{previewInvoiceTag}</p>
                      </div>
                    </div>
                    
                    {/* Desktop: Original layout */}
                    <div className="hidden md:flex items-center space-x-3 min-w-0">
                      <div 
                        className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center cursor-pointer relative"
                        style={{
                          background: 'rgba(167, 238, 67, 0.15)',
                          border: '1px solid rgba(167, 238, 67, 0.3)'
                        }}
                        onClick={() => navigate('/dashboard')}
                      >
                        <span 
                          className="font-medium text-xs"
                          style={{ 
                            color: 'var(--primary)'
                          }}
                        >
                          invo
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-foreground shrink-0">Create Invoice</p>
                      <p className="text-sm text-muted-foreground ml-4 truncate min-w-0">{previewInvoiceTag}</p>
                    </div>
                  </div>
                </div>
                {/* Smaller screens: same control style as ThemeToggle (see ThemeToggle.js) */}
                <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0 md:hidden">
                  <StartHereButton isDark={isDark} mobile onClick={startTutorial} />
                  <a
                    href={STRIPE_DONATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Donate"
                    aria-label="Donate"
                    className={`flex items-center justify-center w-8 h-8 rounded-full bg-card border border-transparent hover:bg-muted transition-all duration-200 group focus:outline-none focus:ring-0 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
                  >
                    <Heart
                      className={`w-4 h-4 transition-colors ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'}`}
                      aria-hidden
                    />
                  </a>
                  <a
                    href={FEEDBACK_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Leave feedback"
                    aria-label="Leave feedback"
                    className={`flex items-center justify-center w-8 h-8 rounded-full bg-card border border-transparent hover:bg-muted transition-all duration-200 group focus:outline-none focus:ring-0 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
                  >
                    <MessageSquare
                      className={`w-4 h-4 transition-colors ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'}`}
                      aria-hidden
                    />
                  </a>
                  <button
                    type="button"
                    onClick={toggleTheme}
                    title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
                    className={`flex items-center justify-center w-8 h-8 rounded-full bg-card border border-transparent hover:bg-muted transition-all duration-200 group focus:outline-none focus:ring-0 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
                  >
                    {isDark ? (
                      <Sun className="w-4 h-4 text-[#edff00] group-hover:text-[#d4e600] transition-colors" />
                    ) : (
                      <Moon className="w-4 h-4 text-slate-600 group-hover:text-slate-500 transition-colors" />
                    )}
                  </button>
                </div>
              </div>
              <div className="hidden md:flex flex-wrap items-center gap-2 md:shrink-0">
                <StartHereButton isDark={isDark} onClick={startTutorial} />
                <a
                  href={STRIPE_DONATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Donate"
                  aria-label="Donate"
                  className={DESKTOP_HEADER_NAV_PILL_CLASS}
                  style={DESKTOP_HEADER_NAV_PILL_STYLE}
                >
                  <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <Heart className="w-3 h-3 nav-text" aria-hidden />
                  </div>
                  <span className="hidden lg:block nav-text">Donate</span>
                </a>
                <a
                  href={FEEDBACK_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Leave feedback"
                  aria-label="Leave feedback"
                  className={DESKTOP_HEADER_NAV_PILL_CLASS}
                  style={DESKTOP_HEADER_NAV_PILL_STYLE}
                >
                  <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-3 h-3 nav-text" aria-hidden />
                  </div>
                  <span className="hidden lg:block nav-text">Feedback</span>
                </a>
              </div>
            </div>
            
            {/* Right Side Controls — desktop only (mobile theme lives in row above) */}
            <div className="hidden md:flex items-center space-x-0.5 md:space-x-1 mr-4 md:mr-4">
              {/* Desktop Navigation Bar */}
              <div className="hidden md:block">
                <div className="flex items-center space-x-4">
                  {/* Main Navigation Bar */}
                  <motion.div 
                    className="px-0.5 py-1.5"
                    style={{
                      borderRadius: '9999px',
                      height: '56px',
                      minHeight: '56px'
                    }}
                  >
                    <div className="flex items-center space-x-4" style={{ height: '100%', alignItems: 'center', paddingRight: '8px' }}>

                      {/* Navigation Tabs */}
                      <div className="flex space-x-1 p-1" style={{ height: '40px', alignItems: 'center' }}>
                        {navigationTabs.map((tab) => (
                          <div key={tab.path} className="relative">
                            <motion.button
                              onClick={() => navigate(tab.path)}
                              className={`group flex items-center space-x-2 px-4 py-2 rounded-[20px] text-sm font-medium transition-all duration-200 border ${
                                activeTab === tab.path
                                  ? 'border-primary/50 text-primary'
                                  : 'border-transparent nav-text hover:border-white/30'
                              }`}
                              style={{
                                background: activeTab === tab.path ? 'rgba(167, 238, 67, 0.12)' : 'transparent',
                                backdropFilter: activeTab === tab.path ? 'blur(20px)' : 'none',
                                WebkitBackdropFilter: activeTab === tab.path ? 'blur(20px)' : 'none',
                                boxShadow: activeTab === tab.path 
                                  ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)' 
                                  : 'none',
                                height: '36px',
                                minWidth: 'fit-content',
                                flexShrink: 0,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <tab.icon 
                                size={16} 
                                className={activeTab === tab.path ? 'text-primary' : 'nav-text'} 
                              />
                              <span className="hidden lg:block" style={{ 
                                textShadow: 'none',
                                WebkitTextStroke: 'none',
                                textStroke: 'none',
                                whiteSpace: 'nowrap'
                              }}>{tab.label}</span>
                            </motion.button>
                          </div>
                        ))}
                      </div>

                      {/* Theme Toggle - Animated Switch */}
                      <div className="relative">
                        <AnimatedThemeToggle />
                      </div>

                      {/* Hide/Show Preview Button */}
                      <div className="relative hidden lg:block">
                        <button
                          onClick={() => setShowPreview(!showPreview)}
                          className="group flex items-center space-x-2 px-3 py-2 rounded-[20px] text-sm font-medium transition-all duration-200 border border-transparent nav-text hover:border-white/30 focus:outline-none"
                          style={{
                            background: 'transparent',
                            height: '36px',
                            minWidth: 'fit-content',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                          title={showPreview ? "Hide Preview" : "Show Preview"}
                        >
                          <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
                            {showPreview ? (
                              <EyeOff className="w-3 h-3 nav-text" />
                            ) : (
                              <Eye className="w-3 h-3 nav-text" />
                            )}
                          </div>
                          <span className="hidden lg:block nav-text">{showPreview ? "Hide" : "Show"}</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 flex overflow-visible mt-0 ${viewMode === 'mobile' ? 'pb-28 md:pb-32' : ''}`}>
          {viewMode === 'desktop' ? (
            /* Desktop Split Screen Layout */
            <>
              {/* Left Panel - Form Editor */}
              <div className={`${showPreview ? 'w-1/2' : 'w-full'} overflow-y-auto transition-all duration-300`}>
                <div className="p-4">
                  {/* Form Content */}
          <div className="space-y-4">
            
            {/* Business Information */}
            <motion.div 
              className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'business')}
              data-tutorial-section="business"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              <div 
                className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                onClick={() => handleSectionToggle('business')}
              >
                <div className="flex items-center space-x-3">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Business Information</h3>
                </div>
                {showBusinessInfo ? (
                  <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                )}
              </div>
              
              <AnimatePresence>
                {showBusinessInfo && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0">
                      {renderTutorialGuide('business')}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-foreground mb-2 block">Business Logo</label>
                          <div className="space-y-3">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="w-full border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary hover:border-[#edff00] transition-all duration-200 file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#edff00] file:text-black hover:file:bg-[#d4e600] file:cursor-pointer cursor-pointer"
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {uploadedLogo && (
                              <div className="flex items-center space-x-3">
                                <img 
                                  src={uploadedLogo} 
                                  alt="Business logo preview" 
                                  className="w-12 h-12 object-contain border border-border rounded"
                                />
                                <button
                                  type="button"
                                  onClick={removeUploadedLogo}
                                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                                >
                                  Remove Logo
                                </button>
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Upload your business logo (max 5MB, JPG/PNG/GIF)
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Business Name *</label>
                          <input
                            type="text"
                            placeholder="Enter business name"
                            value={formData.businessName}
                            onChange={(e) => updateBusinessField('businessName', e.target.value)}
                            maxLength={45}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessName ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessName && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessName}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Business Email *</label>
                          <input
                            type="email"
                            placeholder="business@example.com"
                            value={formData.businessEmail}
                            onChange={(e) => updateBusinessField('businessEmail', e.target.value)}
                            maxLength={45}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessEmail ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessEmail && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessEmail}</p>
                          )}
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Business Phone</label>
                          <input
                            type="tel"
                            placeholder="+1 (555) 123-4567"
                            value={formData.businessPhone}
                            onChange={(e) => updateBusinessField('businessPhone', e.target.value)}
                            maxLength={30}
                            className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Website</label>
                          <input
                            type="url"
                            placeholder="https://www.example.com"
                            value={formData.businessWebsite}
                            onChange={(e) => updateBusinessField('businessWebsite', e.target.value)}
                            maxLength={45}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessWebsite ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessWebsite && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessWebsite}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Business Address *</label>
                          <input
                            type="text"
                            placeholder="123 Business Street"
                            value={formData.businessAddress}
                            onChange={(e) => updateBusinessField('businessAddress', e.target.value)}
                            maxLength={45}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessAddress ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessAddress && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessAddress}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Tax ID / EIN</label>
                          <input
                            type="text"
                            placeholder="12-3456789"
                            value={formData.businessTaxId}
                            onChange={(e) => updateBusinessField('businessTaxId', e.target.value)}
                            maxLength={45}
                            className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">City *</label>
                          <input
                            type="text"
                            placeholder="City"
                            value={formData.businessCity}
                            onChange={(e) => updateBusinessField('businessCity', e.target.value)}
                            maxLength={50}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessCity ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessCity && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessCity}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">State *</label>
                          <input
                            type="text"
                            placeholder="State"
                            value={formData.businessState}
                            onChange={(e) => updateBusinessField('businessState', e.target.value)}
                            maxLength={50}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessState ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessState && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessState}</p>
                          )}
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">ZIP Code *</label>
                          <input
                            type="text"
                            placeholder="12345"
                            value={formData.businessZipCode}
                            onChange={(e) => updateBusinessField('businessZipCode', e.target.value)}
                            maxLength={20}
                            className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                              validationErrors.businessZipCode ? 'border-red-500' : 'border-border'
                            }`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {validationErrors.businessZipCode && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.businessZipCode}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Country</label>
                          <input
                            type="text"
                            placeholder="Country"
                            value={formData.businessCountry}
                            onChange={(e) => updateBusinessField('businessCountry', e.target.value)}
                            maxLength={50}
                            className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Customer Selection */}

          {/* Client Information */}
            <motion.div 
              className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'client')}
              data-tutorial-section="client"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div 
                className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                onClick={() => handleSectionToggle('client')}
              >
                <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Client Information</h3>
                </div>
                {showClientInfo ? (
                  <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                )}
              </div>
              
              <AnimatePresence>
                {showClientInfo && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
              <div className="p-6 pt-0">
                {renderTutorialGuide('client')}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Client Name *</label>
                    <input
                      type="text"
                      placeholder="Enter client name"
                      value={formData.customerName}
                      onChange={(e) => updateCustomerField('customerName', e.target.value)}
                      maxLength={45}
                      className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                        validationErrors.customerName ? 'border-red-500' : 'border-border'
                      }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                    {validationErrors.customerName && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.customerName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Email *</label>
                    <input
                      type="email"
                      placeholder="client@example.com"
                      value={formData.customerEmail}
                      onChange={(e) => updateCustomerField('customerEmail', e.target.value)}
                      maxLength={45}
                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                      validationErrors.customerEmail ? 'border-red-500' : 'border-border'
                    }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                  {validationErrors.customerEmail && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.customerEmail}</p>
                  )}
                </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Phone</label>
                    <input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.customerPhone}
                      onChange={(e) => updateCustomerField('customerPhone', e.target.value)}
                      maxLength={30}
                      className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                       </div>
                  
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Address *</label>
                    <input
                      type="text"
                      placeholder="123 Main Street"
                      value={formData.customerAddress}
                      onChange={(e) => updateCustomerField('customerAddress', e.target.value)}
                      maxLength={45}
                      className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                        validationErrors.customerAddress ? 'border-red-500' : 'border-border'
                      }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                    {validationErrors.customerAddress && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.customerAddress}</p>
                    )}
                             </div>
                  
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">City *</label>
                    <input
                      type="text"
                      placeholder="City"
                      value={formData.customerCity}
                      onChange={(e) => updateCustomerField('customerCity', e.target.value)}
                      maxLength={50}
                      className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                        validationErrors.customerCity ? 'border-red-500' : 'border-border'
                      }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                    {validationErrors.customerCity && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.customerCity}</p>
                    )}
                           </div>
                  
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">State *</label>
                    <input
                      type="text"
                      placeholder="State"
                      value={formData.customerState}
                      onChange={(e) => updateCustomerField('customerState', e.target.value)}
                      maxLength={50}
                      className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                        validationErrors.customerState ? 'border-red-500' : 'border-border'
                      }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                    {validationErrors.customerState && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.customerState}</p>
                    )}
              </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">ZIP Code *</label>
                    <input
                      type="text"
                      placeholder="12345"
                      maxLength={20}
                      value={formData.customerZipCode}
                      onChange={(e) => updateCustomerField('customerZipCode', e.target.value)}
                      className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                        validationErrors.customerZipCode ? 'border-red-500' : 'border-border'
                      }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                    {validationErrors.customerZipCode && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.customerZipCode}</p>
                    )}
                </div>
                  
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Country</label>
                    <input
                      type="text"
                      placeholder="Country"
                      maxLength={50}
                      value={formData.customerCountry}
                      onChange={(e) => updateCustomerField('customerCountry', e.target.value)}
                      className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                      style={{ backgroundColor: 'var(--input)' }}
                    />
                  </div>
                </div>
              </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            

            {/* Invoice Details */}
            <motion.div 
              className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'invoiceDetails')}
              data-tutorial-section="invoiceDetails"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div 
                className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                onClick={() => handleSectionToggle('invoiceDetails')}
              >
                <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Invoice Details</h3>
                </div>
                {showInvoiceDetails ? (
                  <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                )}
              </div>
              
              <AnimatePresence>
                {showInvoiceDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-visible"
                  >
              <div className="p-6 pt-0">
              {renderTutorialGuide('invoiceDetails')}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Due Date *</label>
                  <div className="relative" data-date-picker>
                    <button
                      type="button"
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full text-left flex items-center justify-between ${
                        validationErrors.dueDate ? 'border-red-500' : 'border-border'
                      }`}
                      style={{ backgroundColor: 'var(--input)' }}
                    >
                      <span>{formatDateForDisplay(formData.dueDate) || 'Select date'}</span>
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                    </button>
                    {validationErrors.dueDate && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.dueDate}</p>
                    )}
                    
                    <AnimatePresence>
                      {showDatePicker && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="absolute z-[99999] mt-1 bg-card border border-border rounded-lg shadow-lg p-4 min-w-[280px]"
                          style={{
                            top: '100%',
                            left: '0',
                            right: 'auto'
                          }}
                        >
                          <CalendarPicker 
                            selectedDate={formData.dueDate}
                            onDateSelect={handleDateSelect}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Notes</label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Optional notes..."
                    className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                         style={{ backgroundColor: 'var(--input)' }}
                  />
                </div>
              </div>
              </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Payment Details */}
            <motion.div 
              className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'paymentDetails')}
              data-tutorial-section="paymentDetails"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
            >
              <div 
                className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                onClick={() => handleSectionToggle('paymentDetails')}
              >
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Payment Details</h3>
                </div>
                {showPaymentDetails ? (
                  <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                )}
              </div>
              
              <AnimatePresence>
                {showPaymentDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 space-y-4">
                      {renderTutorialGuide('paymentDetails')}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">IBAN</label>
                          <input
                            type="text"
                            value={formData.iban}
                            onChange={(e) => handlePaymentFieldChange('iban', e.target.value, formatIBAN)}
                            placeholder="GB82 WEST 1234 5698 7654 32"
                            className={`border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${paymentValidationErrors.iban ? 'border-red-500 focus:border-red-500' : ''}`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {paymentValidationErrors.iban && (
                            <div className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {paymentValidationErrors.iban}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">BIC</label>
                          <input
                            type="text"
                            value={formData.bic}
                            onChange={(e) => handlePaymentFieldChange('bic', e.target.value.toUpperCase())}
                            placeholder="DEUTDEFF"
                            className={`border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${paymentValidationErrors.bic ? 'border-red-500 focus:border-red-500' : ''}`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {paymentValidationErrors.bic && (
                            <div className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {paymentValidationErrors.bic}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Sort Code</label>
                          <input
                            type="text"
                            value={formData.sortCode}
                            onChange={(e) => handlePaymentFieldChange('sortCode', e.target.value, formatSortCode)}
                            placeholder="12-34-56"
                            className={`border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${paymentValidationErrors.sortCode ? 'border-red-500 focus:border-red-500' : ''}`}
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                          {paymentValidationErrors.sortCode && (
                            <div className="text-xs text-red-500 mt-1 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {paymentValidationErrors.sortCode}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">Account Number</label>
                          <input
                            type="text"
                            value={formData.accountNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
                            placeholder="12345678"
                            className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                            style={{ backgroundColor: 'var(--input)' }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">Payment Link</label>
                        <input
                          type="url"
                          value={formData.paymentLink}
                          onChange={(e) => setFormData(prev => ({ ...prev, paymentLink: e.target.value }))}
                          placeholder="https://pay.example.com/invoice-123"
                          className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                          style={{ backgroundColor: 'var(--input)' }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

                         {/* Line Items */}
             <motion.div 
               className={getTutorialSectionClassName("bg-card border border-border rounded-xl overflow-hidden", 'lineItems')}
               data-tutorial-section="lineItems"
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.3 }}
             >
               <div 
                 className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                 onClick={() => handleSectionToggle('lineItems')}
               >
                 <div className="flex items-center space-x-3">
                 <ShoppingCart className="w-5 h-5 text-primary" />
                 <h3 className="text-lg font-semibold text-foreground">Line Items</h3>
                 </div>
                 {showLineItems ? (
                   <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                 ) : (
                   <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                 )}
               </div>
               
               <AnimatePresence>
                 {showLineItems && (
                   <motion.div
                     initial={{ height: 0, opacity: 0 }}
                     animate={{ height: 'auto', opacity: 1 }}
                     exit={{ height: 0, opacity: 0 }}
                     transition={{ duration: 0.3, ease: 'easeInOut' }}
                     className="overflow-hidden"
                   >
               <div className="p-6 pt-0">
              {renderTutorialGuide('lineItems')}

               {/* Quick Add Templates */}
               <div className="mb-4 p-3 bg-secondary rounded-lg border border-border">
                 <div className="flex items-center justify-between mb-3">
                   <div className="text-sm font-medium text-foreground">Quick Add Common Items:</div>
                   <div className="relative" data-job-category-dropdown>
                     <button
                       onClick={() => setShowJobCategoryDropdown(!showJobCategoryDropdown)}
                       className={`flex items-center space-x-2 px-3 py-2 rounded-full bg-card border border-transparent hover:bg-muted transition-all duration-200 group focus:outline-none focus:ring-0 ${isDark ? 'hover:border-[#edff00]/60' : 'hover:border-[#4c730f]/60'}`}
                       style={{ outline: 'none', boxShadow: 'none' }}
                     >
                       <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                         <FileText className={`w-4 h-4 transition-colors ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'}`} />
                       </div>
                       <span className="text-sm">{selectedJobCategory}</span>
                       <ChevronDown className={`w-4 h-4 transition-colors transition-transform duration-300 ease-out ${isDark ? 'text-[#edff00] group-hover:text-[#d4e600]' : 'text-slate-600 group-hover:text-slate-500'} ${showJobCategoryDropdown ? 'rotate-180' : ''}`} />
                     </button>
                     
                     <AnimatePresence>
                       {showJobCategoryDropdown && (
                         <motion.div
                           initial={{ opacity: 0, y: -10 }}
                           animate={{ opacity: 1, y: 0 }}
                           exit={{ opacity: 0, y: -10 }}
                           transition={{ duration: 0.2 }}
                           className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[200px] max-h-[300px] overflow-y-auto"
                         >
                           {Object.keys(jobTemplates).map((category) => (
                             <button
                               key={category}
                               onClick={() => {
                                 setSelectedJobCategory(category);
                                 setShowJobCategoryDropdown(false);
                               }}
                               className={`w-full px-3 py-2 text-sm text-left transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                 selectedJobCategory === category ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                               }`}
                             >
                               {category}
                             </button>
                           ))}
                         </motion.div>
                       )}
                     </AnimatePresence>
                   </div>
                 </div>
                 <div className="flex flex-wrap gap-2" role="group" aria-label="Quick add common work items">
                   {workItemTemplates.map((template, index) => (
                     <button
                       key={index}
                       type="button"
                       onClick={() => addTemplateItem(template)}
                       className="px-2 py-1 text-xs border border-[#edff00]/50 text-white bg-[rgba(237,255,0,0.12)] hover:border-[#edff00] focus:border-[#edff00] focus:outline-none focus:ring-2 focus:ring-[#edff00]/30 focus:ring-offset-1 focus:ring-offset-background rounded-[20px] transition-all duration-200 hover:shadow-sm focus:shadow-sm min-h-[32px] backdrop-blur-sm"
                       aria-label={`Add ${template.description} to invoice`}
                     >
                       {template.description}
                     </button>
                   ))}
                 </div>
               </div>
               
               {/* Line Items Table */}
               <div 
                 className="space-y-3"
                 data-line-items-section
                 onClick={(e) => {
                   // If clicking on the container itself (not on child elements), minimize any expanded items
                   if (e.target === e.currentTarget) {
                     setActiveItemId(null);
                   }
                 }}
               >
                 {/* Table Header - Hidden on mobile */}
                 <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border">
                   <div className="col-span-5">Description</div>
                   <div className="col-span-2">Quantity</div>
                   <div className="col-span-2">Rate/Price</div>
                   <div className="col-span-2">VAT</div>
                   <div className="col-span-1"></div>
                 </div>

                 {/* Line Items */}
                 <AnimatePresence mode="popLayout">
                   {formData.lineItems.map((item, index) => (
                     <motion.div 
                       key={item.id}
                       layout
                       initial={{ opacity: 0, y: -10, scale: 0.95 }}
                       animate={{ opacity: 1, y: 0, scale: 1 }}
                       exit={{ 
                         opacity: 0, 
                         x: -100, 
                         scale: 0.95,
                         transition: { duration: 0.2 }
                       }}
                       transition={{ duration: 0.2 }}
                        className={`flex flex-col sm:grid sm:grid-cols-12 gap-3 sm:gap-2 sm:items-center rounded-lg transition-all duration-300 ease-in-out cursor-pointer ${
                          activeItemId === item.id ? 'bg-secondary border border-primary p-4' : 'bg-secondary hover:bg-bg-hover border-2 border-transparent p-4 sm:pt-4 sm:pb-1 sm:px-4'
                        }`}
                       onClick={() => setActiveItemId(item.id)}
                     >
                     {/* Description */}
                     <div className="w-full sm:col-span-5">
                      <label className="block text-xs text-muted-foreground mb-1 sm:hidden">Description</label>
                      <input
                        type="text"
                        placeholder="What work was completed?"
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        maxLength={200}
                        className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                        style={{ backgroundColor: 'var(--input)' }}
                      />
                     </div>

                     {/* Mobile: Quantity, Rate, VAT in a row */}
                     <div className="flex sm:contents gap-2 w-full">
                       {/* Quantity */}
                       <div className="flex-1 sm:col-span-2">
                         <label className="block text-xs text-muted-foreground mb-1 sm:hidden">Qty</label>
                         <input
                           type="number"
                           step="0.25"
                           min="0"
                           placeholder="Qty"
                           value={item.quantity}
                           onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                           className="border border-border rounded-full px-3 sm:px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                           style={{ backgroundColor: 'var(--input)' }}
                         />
                       </div>

                       {/* Rate */}
                       <div className="flex-1 relative sm:col-span-2">
                         <label className="block text-xs text-muted-foreground mb-1 sm:hidden">Rate</label>
                         <input
                           type="number"
                           step="0.01"
                           min="0"
                           placeholder="Rate"
                           value={item.rate}
                           onChange={(e) => updateLineItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                           className="border border-border rounded-full px-3 sm:px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                           style={{ backgroundColor: 'var(--input)' }}
                         />
                       </div>

                       {/* Tax */}
                       <div className="flex-1 relative sm:col-span-2">
                         <label className="block text-xs text-muted-foreground mb-1 sm:hidden">VAT</label>
                         <input
                           type="number"
                           min="0"
                           max="100"
                           step="0.1"
                           placeholder="VAT"
                           value={item.taxRate || ''}
                           onChange={(e) => updateLineItem(item.id, 'taxRate', parseFloat(e.target.value) || 0)}
                           className="border border-border rounded-full px-3 sm:px-4 py-2 pr-8 text-sm text-foreground focus:outline-none focus:border-primary w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                           style={{ backgroundColor: 'var(--input)' }}
                           title="Tax rate percentage"
                         />
                         <span className={`absolute right-3 bottom-2 sm:top-1/2 sm:transform sm:-translate-y-1/2 text-sm font-medium pointer-events-none ${isDark ? 'text-[#edff00]' : 'text-slate-600'}`}>%</span>
                       </div>

                       {/* Actions - visible on mobile within the row */}
                       <div className="flex items-end sm:col-span-1 sm:justify-center pb-1 sm:pb-0">
                         {formData.lineItems.length > 1 && (
                           <button
                             type="button"
                             onClick={(e) => { e.stopPropagation(); removeLineItem(item.id); }}
                             className="text-red-400 hover:text-red-300 p-1 rounded transition-colors"
                             title="Remove this item"
                           >
                             <Trash2 className="h-4 w-4" />
                           </button>
                         )}
                       </div>
                     </div>

                     {/* Inline controls (only under active item) */}
                     <AnimatePresence initial={false}>
                     {activeItemId === item.id && (
                       <motion.div
                         className="col-span-12 mt-3"
                         initial={{ height: 0, opacity: 0, y: -8 }}
                         animate={{ height: 'auto', opacity: 1, y: 0 }}
                         exit={{ height: 0, opacity: 0, y: -8 }}
                         transition={{ duration: 0.25, ease: 'easeInOut' }}
                       >
                         <div className="space-y-3">
                           {/* Notes field */}
                           <div>
                             <label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</label>
                             <textarea
                              placeholder="Add any additional notes for this item..."
                              value={item.notes || ''}
                              onChange={(e) => updateLineItem(item.id, 'notes', e.target.value)}
                              maxLength={300}
                              className="w-full border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary resize-none"
                              style={{ backgroundColor: 'var(--input)' }}
                              rows={2}
                            />
                           </div>
                           
                           <div className="flex items-center justify-between">
                             <div className="text-sm text-primary font-medium">
                               Amount: {getCurrencySymbol(formData.currency)}{(item.quantity * item.rate).toFixed(2)}
                           </div>
                           <button
                             type="button"
                             onClick={(e) => {
                               e.stopPropagation();
                               addLineItem();
                             }}
                             className="btn btn-primary btn-sm flex items-center space-x-2"
                             data-add-item
                           >
                             <Plus className="h-4 w-4" />
                             <span>Add Item</span>
                           </button>
                           </div>
                         </div>
                       </motion.div>
                     )}
                     </AnimatePresence>
                     </motion.div>
                   ))}
                 </AnimatePresence>
               </div>

               {/* Summary */}
               <div className="mt-4 p-3 bg-secondary rounded-lg border border-border">
                 <div className="flex flex-wrap gap-2 sm:gap-4 justify-between items-center text-sm">
                   <span className="text-muted-foreground">Total Items: {formData.lineItems.length}</span>
                   <span className="text-muted-foreground">Subtotal: {getCurrencySymbol(formData.currency)}{totals.subtotal.toFixed(2)}</span>
                   {totals.taxAmount > 0 && (
                     <span className="text-muted-foreground">VAT: {getCurrencySymbol(formData.currency)}{totals.taxAmount.toFixed(2)}</span>
                   )}
                   <span className="text-primary font-semibold">Total: {getCurrencySymbol(formData.currency)}{totals.total.toFixed(2)}</span>
                 </div>
               </div>
               </div>
                   </motion.div>
                 )}
               </AnimatePresence>
             </motion.div>

                  </div>
            </div>
          </div>

              {/* Right Panel - Live Preview */}
              <AnimatePresence>
          {showPreview && (
            <motion.div 
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: '50%', opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="h-full overflow-y-auto rounded-xl pt-4 sticky top-0"
                  >
                    
                    <InvoiceLivePreview
                      invoice={convertToInvoiceFormat()}
                      companySettings={{
                        companyName: formData.businessName || 'Your Company',
                        email: formData.businessEmail || '',
                        phone: formData.businessPhone || '',
                        website: formData.businessWebsite || '',
                        taxId: formData.businessTaxId || '',
                        address: {
                          street: formData.businessAddress || '',
                          city: formData.businessCity || '',
                          state: formData.businessState || '',
                          zipCode: formData.businessZipCode || '',
                          country: formData.businessCountry || ''
                        },
                        logo: companySettings?.logo
                      }}
                      formattedAddress={getFormattedBusinessAddress()}
                      customization={customStyles}
                      uploadedLogo={uploadedLogo}
                      template={selectedTemplate}
                      availableTemplates={availableTemplates}
                      templateConfig={templateConfig}
                      onTemplateChange={navigateTemplate}
                      customizationStyles={customStyles}
                      updateCustomStyle={updateCustomStyle}
                      showDisplayOptionsDropdown={showDisplayOptionsDropdown}
                      setShowDisplayOptionsDropdown={setShowDisplayOptionsDropdown}
                      isDark={isDark}
                      // Currency props
                      currency={formData.currency}
                      onCurrencyChange={updateCurrency}
                      showCurrencyDropdown={showCurrencyDropdown}
                      setShowCurrencyDropdown={setShowCurrencyDropdown}
                      getCurrencySymbol={getCurrencySymbol}
                      // Download props
                      onDownloadPDF={handleDownloadPDF}
                      isGeneratingPDF={generatingPDF}
                      // Form data for validation
                      formData={formData}
                      validationErrors={validationErrors}
                      setValidationErrors={setValidationErrors}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            /* Mobile Tab Layout */
            <div className="w-full overflow-y-auto">
              <AnimatePresence mode="wait" initial={false}>
              {mobileTab === 'edit' ? (
                <motion.div
                  key="mobile-edit-panel"
                  initial={{ x: -26 * mobileTabSlideDirection, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 26 * mobileTabSlideDirection, opacity: 0 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  className="p-4"
                >
                  {/* Form Content */}
                  <div className="space-y-4">
                    {/* Business Information */}
                    <motion.div 
                      className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'business')}
                      data-tutorial-section="business"
                      initial={!hasPlayedMobileEditIntroRef.current ? { opacity: 0, y: 20 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.05 }}
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                        onClick={() => handleSectionToggle('business')}
                      >
                        <div className="flex items-center space-x-3">
                          <Building2 className="w-5 h-5 text-primary" />
                          <h3 className="text-lg font-semibold text-foreground">Business Information</h3>
                        </div>
                        {showBusinessInfo ? (
                          <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        )}
                      </div>
                      
                      <AnimatePresence>
                        {showBusinessInfo && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 pt-0">
                              {renderTutorialGuide('business')}
                              <div className="grid grid-cols-1 gap-4">
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Business Logo</label>
                                  <div className="space-y-3">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={handleLogoUpload}
                                      className="w-full border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary hover:border-[#edff00] transition-all duration-200 file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#edff00] file:text-black hover:file:bg-[#d4e600] file:cursor-pointer cursor-pointer"
                                      style={{ backgroundColor: 'var(--input)' }}
                                    />
                                    {uploadedLogo && (
                                      <div className="flex items-center space-x-3">
                                        <img 
                                          src={uploadedLogo} 
                                          alt="Business logo preview" 
                                          className="w-12 h-12 object-contain border border-border rounded"
                                        />
                                        <button
                                          type="button"
                                          onClick={removeUploadedLogo}
                                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                                        >
                                          Remove Logo
                                        </button>
                                      </div>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                      Upload your business logo (max 5MB, JPG/PNG/GIF)
                                    </p>
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Business Name *</label>
                                  <input
                                    type="text"
                                    placeholder="Enter business name"
                                    value={formData.businessName}
                                    onChange={(e) => updateBusinessField('businessName', e.target.value)}
                                    maxLength={45}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessName ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessName && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessName}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Business Email *</label>
                                  <input
                                    type="email"
                                    placeholder="business@example.com"
                                    value={formData.businessEmail}
                                    onChange={(e) => updateBusinessField('businessEmail', e.target.value)}
                                    maxLength={45}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessEmail ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessEmail && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessEmail}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Business Phone</label>
                                  <input
                                    type="tel"
                                    placeholder="+1 (555) 123-4567"
                                    value={formData.businessPhone}
                                    onChange={(e) => updateBusinessField('businessPhone', e.target.value)}
                                    className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Website</label>
                                  <input
                                    type="url"
                                    placeholder="https://www.example.com"
                                    value={formData.businessWebsite}
                                    onChange={(e) => updateBusinessField('businessWebsite', e.target.value)}
                                    maxLength={45}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessWebsite ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessWebsite && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessWebsite}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Business Address *</label>
                                  <input
                                    type="text"
                                    placeholder="123 Business Street"
                                    value={formData.businessAddress}
                                    onChange={(e) => updateBusinessField('businessAddress', e.target.value)}
                                    maxLength={45}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessAddress ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessAddress && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessAddress}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Tax ID / EIN</label>
                                  <input
                                    type="text"
                                    placeholder="12-3456789"
                                    value={formData.businessTaxId}
                                    onChange={(e) => updateBusinessField('businessTaxId', e.target.value)}
                                    maxLength={45}
                                    className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">City *</label>
                                  <input
                                    type="text"
                                    placeholder="City"
                                    value={formData.businessCity}
                                    onChange={(e) => updateBusinessField('businessCity', e.target.value)}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessCity ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessCity && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessCity}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">State *</label>
                                  <input
                                    type="text"
                                    placeholder="State"
                                    value={formData.businessState}
                                    onChange={(e) => updateBusinessField('businessState', e.target.value)}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessState ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessState && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessState}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">ZIP Code *</label>
                                  <input
                                    type="text"
                                    placeholder="12345"
                                    value={formData.businessZipCode}
                                    onChange={(e) => updateBusinessField('businessZipCode', e.target.value)}
                                    className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                      validationErrors.businessZipCode ? 'border-red-500' : 'border-border'
                                    }`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {validationErrors.businessZipCode && (
                                    <p className="text-red-500 text-xs mt-1">{validationErrors.businessZipCode}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Country</label>
                                  <input
                                    type="text"
                                    placeholder="Country"
                                    value={formData.businessCountry}
                                    onChange={(e) => updateBusinessField('businessCountry', e.target.value)}
                                    className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Customer Selection */}
                    <motion.div 
                      className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'client')}
                      data-tutorial-section="client"
                      initial={!hasPlayedMobileEditIntroRef.current ? { opacity: 0, y: 20 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                        onClick={() => handleSectionToggle('client')}
                      >
                        <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-foreground">Client Information</h3>
                        </div>
                        {showClientInfo ? (
                          <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        )}
                    </div>
                      
                      <AnimatePresence>
                        {showClientInfo && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                      <div className="p-6 pt-0">
                        {renderTutorialGuide('client')}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Client Name *</label>
                            <input
                              type="text"
                              placeholder="Enter client name"
                              value={formData.customerName}
                              onChange={(e) => updateCustomerField('customerName', e.target.value)}
                              maxLength={45}
                              className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                validationErrors.customerName ? 'border-red-500' : 'border-border'
                              }`}
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {validationErrors.customerName && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.customerName}</p>
                            )}
                    </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Email *</label>
                            <input
                              type="email"
                              placeholder="client@example.com"
                              value={formData.customerEmail}
                              onChange={(e) => updateCustomerField('customerEmail', e.target.value)}
                              maxLength={45}
                              className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                validationErrors.customerEmail ? 'border-red-500' : 'border-border'
                              }`}
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {validationErrors.customerEmail && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.customerEmail}</p>
                            )}
                  </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Phone</label>
                            <input
                              type="tel"
                              placeholder="+1 (555) 123-4567"
                              value={formData.customerPhone}
                              onChange={(e) => updateCustomerField('customerPhone', e.target.value)}
                              className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                  </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Address *</label>
                            <input
                              type="text"
                              placeholder="123 Main Street"
                              value={formData.customerAddress}
                              onChange={(e) => updateCustomerField('customerAddress', e.target.value)}
                              maxLength={45}
                              className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                validationErrors.customerAddress ? 'border-red-500' : 'border-border'
                              }`}
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {validationErrors.customerAddress && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.customerAddress}</p>
                            )}
                </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">City *</label>
                            <input
                              type="text"
                              placeholder="City"
                              value={formData.customerCity}
                              onChange={(e) => updateCustomerField('customerCity', e.target.value)}
                              className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                validationErrors.customerCity ? 'border-red-500' : 'border-border'
                              }`}
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {validationErrors.customerCity && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.customerCity}</p>
                            )}
              </div>

                  <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">State *</label>
                            <input
                              type="text"
                              placeholder="State"
                              value={formData.customerState}
                              onChange={(e) => updateCustomerField('customerState', e.target.value)}
                              className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                validationErrors.customerState ? 'border-red-500' : 'border-border'
                              }`}
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {validationErrors.customerState && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.customerState}</p>
                            )}
                  </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">ZIP Code *</label>
                            <input
                              type="text"
                              placeholder="12345"
                              value={formData.customerZipCode}
                              onChange={(e) => updateCustomerField('customerZipCode', e.target.value)}
                              className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${
                                validationErrors.customerZipCode ? 'border-red-500' : 'border-border'
                              }`}
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                            {validationErrors.customerZipCode && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.customerZipCode}</p>
                            )}
                    </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Country</label>
                            <input
                              type="text"
                              placeholder="Country"
                              value={formData.customerCountry}
                              onChange={(e) => updateCustomerField('customerCountry', e.target.value)}
                              className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                    </div>
                    </div>
                      </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Invoice Details */}
                    <motion.div 
                      className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'invoiceDetails')}
                      data-tutorial-section="invoiceDetails"
                      initial={!hasPlayedMobileEditIntroRef.current ? { opacity: 0, y: 20 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                        onClick={() => handleSectionToggle('invoiceDetails')}
                      >
                        <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-foreground">Invoice Details</h3>
                        </div>
                        {showInvoiceDetails ? (
                          <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        )}
                      </div>
                      
                      <AnimatePresence>
                        {showInvoiceDetails && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-visible"
                          >
                      <div className="p-6 pt-0">
                        {renderTutorialGuide('invoiceDetails')}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Due Date *</label>
                            <div className="relative" data-date-picker>
                              <button
                                type="button"
                                onClick={() => setShowDatePicker(!showDatePicker)}
                                className={`border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full text-left flex items-center justify-between ${
                                  validationErrors.dueDate ? 'border-red-500' : 'border-border'
                                }`}
                                style={{ backgroundColor: 'var(--input)' }}
                              >
                                <span>{formatDateForDisplay(formData.dueDate) || 'Select date'}</span>
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                              </button>
                              {validationErrors.dueDate && (
                                <p className="text-red-500 text-xs mt-1">{validationErrors.dueDate}</p>
                              )}
                              
                              <AnimatePresence>
                                {showDatePicker && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                                    className="absolute z-[99999] mt-1 bg-card border border-border rounded-lg shadow-lg p-4 min-w-[280px]"
                        style={{
                                      top: '100%',
                                      left: '0',
                                      right: 'auto'
                                    }}
                                  >
                                    <CalendarPicker 
                                      selectedDate={formData.dueDate}
                                      onDateSelect={handleDateSelect}
                                    />
                                  </motion.div>
                                )}
                              </AnimatePresence>
                    </div>
                  </div>
                          
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">Notes</label>
                            <input
                              type="text"
                              value={formData.notes}
                              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                              placeholder="Optional notes..."
                              className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                              style={{ backgroundColor: 'var(--input)' }}
                            />
                </div>
              </div>
                      </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Payment Details */}
                    <motion.div 
                      className={getTutorialSectionClassName("bg-card border border-border rounded-xl", 'paymentDetails')}
                      data-tutorial-section="paymentDetails"
                      initial={!hasPlayedMobileEditIntroRef.current ? { opacity: 0, y: 20 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.25 }}
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                        onClick={() => handleSectionToggle('paymentDetails')}
                      >
                        <div className="flex items-center space-x-3">
                          <CreditCard className="w-5 h-5 text-primary" />
                          <h3 className="text-lg font-semibold text-foreground">Payment Details</h3>
                        </div>
                        {showPaymentDetails ? (
                          <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        )}
                      </div>
                      
                      <AnimatePresence>
                        {showPaymentDetails && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 pt-0 space-y-4">
                              {renderTutorialGuide('paymentDetails')}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">IBAN</label>
                                  <input
                                    type="text"
                                    value={formData.iban}
                                    onChange={(e) => handlePaymentFieldChange('iban', e.target.value, formatIBAN)}
                                    placeholder="GB82 WEST 1234 5698 7654 32"
                                    className={`border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${paymentValidationErrors.iban ? 'border-red-500 focus:border-red-500' : ''}`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {paymentValidationErrors.iban && (
                                    <div className="text-xs text-red-500 mt-1 flex items-center">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      {paymentValidationErrors.iban}
                                    </div>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">BIC</label>
                                  <input
                                    type="text"
                                    value={formData.bic}
                                    onChange={(e) => handlePaymentFieldChange('bic', e.target.value.toUpperCase())}
                                    placeholder="DEUTDEFF"
                                    className={`border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${paymentValidationErrors.bic ? 'border-red-500 focus:border-red-500' : ''}`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {paymentValidationErrors.bic && (
                                    <div className="text-xs text-red-500 mt-1 flex items-center">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      {paymentValidationErrors.bic}
                                    </div>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Sort Code</label>
                                  <input
                                    type="text"
                                    value={formData.sortCode}
                                    onChange={(e) => handlePaymentFieldChange('sortCode', e.target.value, formatSortCode)}
                                    placeholder="12-34-56"
                                    className={`border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full ${paymentValidationErrors.sortCode ? 'border-red-500 focus:border-red-500' : ''}`}
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                  {paymentValidationErrors.sortCode && (
                                    <div className="text-xs text-red-500 mt-1 flex items-center">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      {paymentValidationErrors.sortCode}
                                    </div>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-foreground mb-2 block">Account Number</label>
                                  <input
                                    type="text"
                                    value={formData.accountNumber}
                                    onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
                                    placeholder="12345678"
                                    className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                                    style={{ backgroundColor: 'var(--input)' }}
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="text-sm font-medium text-foreground mb-2 block">Payment Link</label>
                                <input
                                  type="url"
                                  value={formData.paymentLink}
                                  onChange={(e) => setFormData(prev => ({ ...prev, paymentLink: e.target.value }))}
                                  placeholder="https://pay.example.com/invoice-123"
                                  className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                                  style={{ backgroundColor: 'var(--input)' }}
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Line Items */}
                    <motion.div 
                      className={getTutorialSectionClassName("bg-card border border-border rounded-xl overflow-hidden", 'lineItems')}
                      data-tutorial-section="lineItems"
                      initial={!hasPlayedMobileEditIntroRef.current ? { opacity: 0, y: 20 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 }}
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200"
                        onClick={() => handleSectionToggle('lineItems')}
                      >
                        <div className="flex items-center space-x-3">
                        <ShoppingCart className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-foreground">Line Items</h3>
                        </div>
                        {showLineItems ? (
                          <ChevronDown className={`w-5 h-5 transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 rotate-[-90deg] transition-colors duration-200 ${isDark ? 'text-muted-foreground group-hover:text-[#edff00]' : 'text-muted-foreground group-hover:text-[#edff00]'}`} />
                        )}
                      </div>
                      
                      <AnimatePresence>
                        {showLineItems && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                      <div className="p-6 pt-0">
                        {renderTutorialGuide('lineItems')}
                        {/* Quick Add Templates */}
                        <div className="mb-4 p-3 bg-secondary rounded-lg border border-border">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-medium text-foreground">Quick Add Common Items:</div>
                            <div className="relative" data-job-category-dropdown>
                              <button
                                onClick={() => setShowJobCategoryDropdown(!showJobCategoryDropdown)}
                                className="btn btn-outline btn-sm p-2 hover:bg-secondary focus:outline-none focus:ring-0 focus:ring-offset-0 active:outline-none active:ring-0 active:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 active:bg-secondary flex items-center space-x-1"
                                style={{ outline: 'none', boxShadow: 'none' }}
                              >
                                <span className="text-xs">{selectedJobCategory}</span>
                                <ChevronDown className={`w-3 h-3 transition-colors ${isDark ? 'text-yellow-500 group-hover:text-yellow-400' : 'text-slate-600 group-hover:text-slate-500'}`} />
                              </button>
                              
                              <AnimatePresence>
                                {showJobCategoryDropdown && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[200px] max-h-[300px] overflow-y-auto"
                                  >
                                    {Object.keys(jobTemplates).map((category) => (
                                      <button
                                        key={category}
                                        onClick={() => {
                                          setSelectedJobCategory(category);
                                          setShowJobCategoryDropdown(false);
                                        }}
                                        className={`w-full px-3 py-2 text-sm text-left transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                          selectedJobCategory === category ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                                        }`}
                                        style={{ backgroundColor: selectedJobCategory === category ? undefined : 'var(--input)' }}
                                      >
                                        {category}
                                      </button>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2" role="group" aria-label="Quick add common work items">
                            {workItemTemplates.map((template, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => addTemplateItem(template)}
                                className="px-2 py-1 text-xs border border-[#edff00]/50 text-white bg-[rgba(237,255,0,0.12)] hover:border-[#edff00] focus:border-[#edff00] focus:outline-none focus:ring-2 focus:ring-[#edff00]/30 focus:ring-offset-1 focus:ring-offset-background rounded-[20px] transition-all duration-200 hover:shadow-sm focus:shadow-sm min-h-[32px] backdrop-blur-sm"
                                aria-label={`Add ${template.description} to invoice`}
                              >
                                {template.description}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Line Items Table */}
                        <div 
                          className="space-y-3"
                          data-line-items-section
                          onClick={(e) => {
                            // If clicking on the container itself (not on child elements), minimize any expanded items
                            if (e.target === e.currentTarget) {
                              setActiveItemId(null);
                            }
                          }}
                        >
                          {/* Table Header - Hidden on mobile */}
                          <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border">
                            <div className="col-span-5">Description</div>
                            <div className="col-span-2">Quantity</div>
                            <div className="col-span-2">Rate/Price</div>
                            <div className="col-span-2">VAT</div>
                            <div className="col-span-1"></div>
                          </div>

                          {/* Line Items */}
                    <AnimatePresence mode="popLayout">
                      {formData.lineItems.map((item, index) => (
                              <motion.div 
                          key={item.id}
                          layout
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ 
                            opacity: 0, 
                                  x: -100, 
                                  scale: 0.95,
                                  transition: { duration: 0.2 }
                                }}
                                transition={{ duration: 0.2 }}
                        className={`flex flex-col sm:grid sm:grid-cols-12 gap-3 sm:gap-2 sm:items-center rounded-lg transition-all duration-300 ease-in-out cursor-pointer ${
                          activeItemId === item.id ? 'bg-secondary border border-primary p-4' : 'bg-secondary hover:bg-bg-hover border-2 border-transparent p-4 sm:pt-4 sm:pb-1 sm:px-4'
                        }`}
                                onClick={() => setActiveItemId(item.id)}
                              >
                                {/* Description */}
                                <div className="w-full sm:col-span-5">
                      <label className="block text-xs text-muted-foreground mb-1 sm:hidden">Description</label>
                      <input
                        type="text"
                        placeholder="What work was completed?"
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        maxLength={200}
                        className="border border-border rounded-full px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full"
                        style={{ backgroundColor: 'var(--input)' }}
                      />
              </div>

                                {/* Mobile: Quantity, Rate, VAT in a row */}
                                <div className="flex sm:contents gap-2 w-full">
                                  {/* Quantity */}
                                  <div className="flex-1 sm:col-span-2">
                                    <label className="block text-xs text-muted-foreground mb-1 sm:hidden">Qty</label>
                                    <input
                                      type="number"
                                      step="0.25"
                                      min="0"
                                      placeholder="Qty"
                                      value={item.quantity}
                                      onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                      className="border border-border rounded-full px-3 sm:px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      style={{ backgroundColor: 'var(--input)' }}
                                    />
                                  </div>
                
                                  {/* Rate */}
                                  <div className="flex-1 relative sm:col-span-2">
                                    <label className="block text-xs text-muted-foreground mb-1 sm:hidden">Rate</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Rate"
                                      value={item.rate}
                                      onChange={(e) => updateLineItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                                      className="border border-border rounded-full px-3 sm:px-4 py-2 text-sm text-foreground focus:outline-none focus:border-primary w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      style={{ backgroundColor: 'var(--input)' }}
                                    />
                                  </div>

                                  {/* Tax */}
                                  <div className="flex-1 relative sm:col-span-2">
                                    <label className="block text-xs text-muted-foreground mb-1 sm:hidden">VAT</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      placeholder="VAT"
                                      value={item.taxRate || ''}
                                      onChange={(e) => updateLineItem(item.id, 'taxRate', parseFloat(e.target.value) || 0)}
                                      className="border border-border rounded-full px-3 sm:px-4 py-2 pr-8 text-sm text-foreground focus:outline-none focus:border-primary w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      style={{ backgroundColor: 'var(--input)' }}
                                      title="Tax rate percentage"
                                    />
                                    <span className={`absolute right-3 bottom-2 sm:top-1/2 sm:transform sm:-translate-y-1/2 text-sm font-medium pointer-events-none ${isDark ? 'text-[#edff00]' : 'text-slate-600'}`}>%</span>
                                  </div>

                                  {/* Actions - visible on mobile within the row */}
                                  <div className="flex items-end sm:col-span-1 sm:justify-center pb-1 sm:pb-0">
                                    {formData.lineItems.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); removeLineItem(item.id); }}
                                        className="text-red-400 hover:text-red-300 p-1 rounded transition-colors"
                                        title="Remove this item"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Inline controls (only under active item) */}
                                <AnimatePresence initial={false}>
                                  {activeItemId === item.id && (
                                    <motion.div
                                      className="col-span-12 mt-3"
                                      initial={{ height: 0, opacity: 0, y: -8 }}
                                      animate={{ height: 'auto', opacity: 1, y: 0 }}
                                      exit={{ height: 0, opacity: 0, y: -8 }}
                                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    >
                                      <div className="space-y-3">
                                        {/* Notes field */}
                                        <div>
                                          <label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</label>
                             <textarea
                              placeholder="Add any additional notes for this item..."
                              value={item.notes || ''}
                              onChange={(e) => updateLineItem(item.id, 'notes', e.target.value)}
                              maxLength={300}
                              className="w-full border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary resize-none"
                              style={{ backgroundColor: 'var(--input)' }}
                              rows={2}
                            />
                                        </div>
                                        
                                        <div className="flex items-center justify-between">
                                          <div className="text-sm text-primary font-medium">
                                            Amount: {getCurrencySymbol(formData.currency)}{(item.quantity * item.rate).toFixed(2)}
                  </div>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            addLineItem();
                                          }}
                                          className="btn btn-primary btn-sm flex items-center space-x-2"
                                          data-add-item
                                        >
                                          <Plus className="h-4 w-4" />
                                          <span>Add Item</span>
                                        </button>
                                        </div>
                </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                            ))}
                          </AnimatePresence>
              </div>

                        {/* Summary */}
                        <div className="mt-4 p-3 bg-secondary rounded-lg border border-border">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Total Items: {formData.lineItems.length}</span>
                            <span className="text-muted-foreground">Subtotal: {getCurrencySymbol(formData.currency)}{totals.subtotal.toFixed(2)}</span>
                            {totals.taxAmount > 0 && (
                              <span className="text-muted-foreground">Tax: {getCurrencySymbol(formData.currency)}{totals.taxAmount.toFixed(2)}</span>
                            )}
                            <span className="text-primary font-semibold">Total: {getCurrencySymbol(formData.currency)}{totals.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Action Buttons */}
                    <div className="flex space-x-4">
                      
                </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="mobile-preview-panel"
                  initial={{ x: -26 * mobileTabSlideDirection, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 26 * mobileTabSlideDirection, opacity: 0 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                >
                  <InvoiceLivePreview
                    invoice={convertToInvoiceFormat()}
                    companySettings={{
                      companyName: formData.businessName || 'Your Company',
                        email: formData.businessEmail || '',
                      phone: formData.businessPhone || '',
                      website: formData.businessWebsite || '',
                      taxId: formData.businessTaxId || '',
                      address: {
                        street: formData.businessAddress || '',
                        city: formData.businessCity || '',
                        state: formData.businessState || '',
                        zipCode: formData.businessZipCode || '',
                        country: formData.businessCountry || ''
                      },
                      logo: companySettings?.logo
                    }}
                    formattedAddress={getFormattedBusinessAddress()}
                    customization={customStyles}
                    uploadedLogo={uploadedLogo}
                    template={selectedTemplate}
                    availableTemplates={availableTemplates}
                    templateConfig={templateConfig}
                    onTemplateChange={navigateTemplate}
                    customizationStyles={customStyles}
                    updateCustomStyle={updateCustomStyle}
                    showDisplayOptionsDropdown={showDisplayOptionsDropdown}
                    setShowDisplayOptionsDropdown={setShowDisplayOptionsDropdown}
                    isDark={isDark}
                    // Currency props
                    currency={formData.currency}
                    onCurrencyChange={updateCurrency}
                    showCurrencyDropdown={showCurrencyDropdown}
                    setShowCurrencyDropdown={setShowCurrencyDropdown}
                    getCurrencySymbol={getCurrencySymbol}
                    // Download props
                    onDownloadPDF={handleDownloadPDF}
                    isGeneratingPDF={generatingPDF}
                    // Form data for validation
                    formData={formData}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                  />
                </motion.div>
              )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Keyboard Shortcuts Help */}
        <div className="fixed bottom-6 left-6 card-modern px-4 py-2 text-sm opacity-0 hover:opacity-100 transition-opacity duration-300">
          <div className="font-medium mb-1 text-foreground">Keyboard Shortcuts</div>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div>Ctrl+Enter: Download PDF</div>
            <div>Ctrl+P: Toggle Preview</div>
          </div>
        </div>

        {/* Validation Errors Toast — collapsible to save screen space */}
        <AnimatePresence>
          {Object.entries(validationErrors).some(([field, error]) => error && error.trim() !== '') && (
            <motion.div
              layout
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: 300 }}
              transition={{ layout: { duration: 0.28, ease: [0.25, 0.1, 0.25, 1] } }}
              className={`fixed z-[60] backdrop-blur-xl border border-white/10 shadow-lg ${
                validationToastCollapsed ? 'rounded-full' : 'rounded-lg'
              } ${
                validationToastCollapsed
                  ? viewMode === 'mobile'
                    ? 'bottom-[5.75rem] right-2 sm:bottom-[6.25rem] sm:right-6 md:bottom-[6.75rem] md:right-8 w-11 h-11 sm:w-auto sm:h-auto'
                    : 'bottom-2 right-2 sm:bottom-6 sm:right-6 w-11 h-11 sm:w-auto sm:h-auto'
                  : viewMode === 'mobile'
                    ? 'bottom-[6.25rem] right-3 sm:bottom-[6.75rem] sm:right-6 md:bottom-[7.25rem] md:right-8 max-w-sm'
                    : 'bottom-4 right-4 sm:bottom-6 sm:right-6 max-w-sm'
              }`}
              style={{
                background: 'rgba(225, 69, 38, 0.8)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
              }}
            >
              {(() => {
                const errorEntries = Object.entries(validationErrors).filter(
                  ([, error]) => error && error.trim() !== ''
                );
                const errorCount = errorEntries.length;
                if (validationToastCollapsed) {
                  return (
                    <motion.button
                      layout="position"
                      type="button"
                      onClick={() => setValidationToastCollapsed(false)}
                      className="w-full h-full rounded-full flex items-center justify-center gap-1.5 sm:gap-2 sm:px-3 sm:py-2 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                      aria-expanded={false}
                      aria-label={`Expand validation list (${errorCount} ${errorCount === 1 ? 'issue' : 'issues'})`}
                      transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
                      animate={{ rotate: 0 }}
                    >
                      <AlertCircle className="w-4 h-4 sm:w-[18px] sm:h-[18px] shrink-0 text-white" aria-hidden />
                      <span className="hidden sm:inline text-sm font-medium leading-none text-white">Validation errors</span>
                      <ChevronDown className="hidden sm:block w-4 h-4 text-white" aria-hidden />
                    </motion.button>
                  );
                }

                return (
                  <div className="px-3 py-2 sm:px-4 sm:py-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle
                        className="w-5 h-5 mt-0.5 shrink-0 text-white"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <motion.h4
                            layout="position"
                            className="font-medium leading-snug pr-1 text-white text-sm"
                            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                          >
                            Please fix the following errors:
                          </motion.h4>
                          <motion.button
                            layout="position"
                            type="button"
                            onClick={() => setValidationToastCollapsed((c) => !c)}
                            className="shrink-0 rounded-lg flex items-center justify-center hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 p-1.5 min-h-[36px] min-w-[36px]"
                            aria-expanded={true}
                            aria-label="Collapse validation list"
                            transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
                            animate={{ rotate: 180 }}
                          >
                            <ChevronDown className="w-4 h-4 text-white" aria-hidden />
                          </motion.button>
                        </div>
                        <motion.div
                          initial={false}
                          animate={{
                            height: validationToastCollapsed ? 0 : 'auto',
                            opacity: validationToastCollapsed ? 0 : 1,
                          }}
                          transition={{
                            duration: 0.28,
                            ease: [0.25, 0.1, 0.25, 1],
                            opacity: { duration: 0.22 },
                          }}
                          className="overflow-hidden"
                          style={{ pointerEvents: validationToastCollapsed ? 'none' : 'auto' }}
                        >
                          <ul className="pt-2 text-sm space-y-1 text-white max-h-[40vh] overflow-y-auto pr-1">
                            {errorEntries.map(([field, error]) => (
                              <li key={field} className="leading-snug">
                                • {error}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Mobile Navigation: Edit / Preview / PDF */}
        {viewMode === 'mobile' && (
          <div
            className={`fixed left-3 right-3 bottom-3 sm:left-6 sm:right-6 sm:bottom-4 md:left-8 md:right-8 md:bottom-5 z-[55] rounded-[22px] border backdrop-blur-xl ${
              isDark ? 'border-white/10' : 'border-[#d1d5db]'
            }`}
            style={{
              background: 'var(--nav-background)',
              boxShadow: 'var(--nav-shadow)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <div className="flex items-center gap-2 p-2">
              <div className={`flex items-center gap-1 p-1 rounded-[18px] flex-1 min-w-0 ${isDark ? 'bg-black/20' : 'bg-[#f3f4f6]'}`}>
                <button
                  onClick={() => {
                    setMobileTabSlideDirection(-1);
                    setMobileTab('edit');
                  }}
                  className={`flex-1 px-3 py-2 rounded-[16px] text-sm font-medium transition-all duration-200 border ${
                    mobileTab === 'edit'
                      ? 'border-primary/50 text-primary'
                      : isDark ? 'border-transparent nav-text hover:border-white/30' : 'border-transparent text-[#3f3f46] hover:border-[#cfd5df]'
                  }`}
                  style={{
                    background: mobileTab === 'edit' ? 'rgba(167, 238, 67, 0.12)' : 'transparent',
                    boxShadow: mobileTab === 'edit'
                      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.12)'
                      : 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span className="sm:hidden">Edit</span>
                  <span className="hidden sm:inline">Edit Invoice</span>
                </button>
                <button
                  onClick={() => {
                    setMobileTabSlideDirection(1);
                    setMobileTab('preview');
                  }}
                  className={`flex-1 px-3 py-2 rounded-[16px] text-sm font-medium transition-all duration-200 border ${
                    mobileTab === 'preview'
                      ? 'border-primary/50 text-primary'
                      : isDark ? 'border-transparent nav-text hover:border-white/30' : 'border-transparent text-[#3f3f46] hover:border-[#cfd5df]'
                  }`}
                  style={{
                    background: mobileTab === 'preview' ? 'rgba(167, 238, 67, 0.12)' : 'transparent',
                    boxShadow: mobileTab === 'preview'
                      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.12)'
                      : 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Preview
                </button>
              </div>

              <button
                type="button"
                onClick={handleDownloadPDF}
                disabled={generatingPDF}
                title={generatingPDF ? 'Generating PDF...' : 'Download PDF'}
                aria-label={generatingPDF ? 'Generating PDF' : 'Download PDF'}
                className={`group flex items-center justify-center gap-1.5 rounded-[18px] border px-3 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-0 disabled:opacity-60 disabled:cursor-not-allowed ${
                  mobileDownloadReady
                    ? (isDark ? 'border-primary/50 text-primary' : 'border-primary/50')
                    : isDark
                      ? 'border-gray-600/30 text-gray-400 invalid'
                      : 'border-gray-300 text-gray-500 invalid'
                }`}
                style={{
                  background: mobileDownloadReady
                    ? (isDark ? 'rgba(167, 238, 67, 0.12)' : '#ffffff')
                    : (isDark ? '#1f2937' : '#f3f4f6'),
                  color: mobileDownloadReady
                    ? (isDark ? '#edff00' : '#1a1a1a')
                    : (isDark ? '#9ca3af' : '#6b7280'),
                  backdropFilter: isDark ? (mobileDownloadReady ? 'blur(20px)' : 'blur(10px)') : 'none',
                  WebkitBackdropFilter: isDark ? (mobileDownloadReady ? 'blur(20px)' : 'blur(10px)') : 'none',
                  boxShadow: mobileDownloadReady
                    ? (isDark ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)')
                    : (isDark ? 'inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'),
                  height: '36px',
                  flexShrink: 0,
                  outline: 'none',
                }}
              >
                {generatingPDF ? (
                  <Loader2
                    className={`w-4 h-4 animate-spin transition-colors ${mobileDownloadReady ? (isDark ? 'text-primary' : 'text-[#1a1a1a]') : (isDark ? 'text-gray-400' : 'text-gray-500')}`}
                    aria-hidden
                  />
                ) : (
                  mobileDownloadReady ? (
                    <Save
                      className={`w-4 h-4 transition-colors ${mobileDownloadReady ? (isDark ? 'text-primary' : 'text-[#1a1a1a]') : (isDark ? 'text-gray-400' : 'text-gray-500')}`}
                      aria-hidden
                    />
                  ) : (
                    <X className={`w-4 h-4 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`} aria-hidden />
                  )
                )}
                <span>{generatingPDF ? 'Saving...' : 'PDF'}</span>
              </button>
            </div>
          </div>
        )}

        <SeoLandingContent />
      </div>
    </Layout>
  );
};

const InvoiceDesigner = () => {
  return <InvoiceDesignerContent />;
};

export default InvoiceDesigner;
